import { TipoLegalizacao } from '../../../hooks/useLegalizacao';
import type { TipoAlteracao } from '../../../hooks/useSocietario';
import { resolverRegistry } from './registries';
import type { RegistryConfig, ViabilidadeSubpasso } from './registries';
import { classificarRotaAlteracao } from '../contratos/criar/classificarRotaAlteracao';

// ============================================================================
// PLAYBOOK — o que cada ETAPA (coluna) de cada TIPO mostra no drawer.
// Port fiel do mockup (`Downloads/mockup-societario-legalizacao.html`, const
// PLAYBOOK). É o coração da UX "por etapa": o drawer renderiza SÓ o bloco da
// coluna atual — campos + checklist + anexos + a ação (⭐ só onde existe).
//
// JUCEG agora (plano §4: autorar 1 junta, outras UFs viram dados depois).
// As COLUNAS (chaves) têm que bater EXATO com os títulos criados pelo backend
// (`legalizacao_kanban_sync._PERFIS`), porque o drawer casa por título.
// ============================================================================

// Origem do valor de um campo da etapa:
//  - 'subtype'  → coluna TIPADA do processo (gop/gon/nire/cnpj/dare_*/protocolo_*/
//                 rota/junta_*/cadastro_confere) — editável via PATCH.
//  - 'estado'   → chave em `estado_etapas` (briefing/valores não-tipados) — PATCH.
//  - 'display'  → só-leitura, derivado (sócios/empresa vêm do contrato/levantamento).
//  - 'tipos'    → input especial: marca o que muda → deriva a rota (só Definição+Rota).
export type CampoOrigem = 'subtype' | 'estado' | 'display' | 'tipos';

/**
 * Regimes da lista do Levantamento. O wizard do contrato tem os mesmos, menos o MEI (16/10, pedido da equipe):
 * MEI não tem contrato social.
 */
export const REGIMES_TRIBUTARIOS = [
  'MEI',
  'Simples Nacional',
  'Lucro Presumido',
  'Lucro Real',
] as const;
export type RegimeTributario = (typeof REGIMES_TRIBUTARIOS)[number];

export interface PlaybookCampo {
  /** nome da coluna do subtype (origem 'subtype') OU chave no estado_etapas ('estado'). */
  key: string;
  label: string;
  origem: CampoOrigem;
  tipo?: 'text' | 'date' | 'number' | 'checkbox' | 'select';
  /** opções da lista (tipo 'select'), gravadas como o próprio texto. */
  opcoes?: readonly string[];
  placeholder?: string;
  /** valor exibido quando origem = 'display'. */
  valorDisplay?: string;
  /** key de um item do checklist que é auto-marcado quando ESTE campo é preenchido (1:1). */
  marca?: string;
  /**
   * o rótulo ganha o termo do registry por papel (GOP→"Protocolo Web", DARE→emolumentos…). `abertura` = o da
   * viabilidade na constituição (GO: GOB), caindo no de `viabilidade` onde a junta não diferencia.
   */
  rotuloRegistry?: 'viabilidade' | 'abertura' | 'coletor' | 'registro' | 'guia';
  /** Só existe com DBE: some na constituição onde o registry tem `aberturaSemDbe` (Balcão Único). */
  soComDbe?: boolean;
  /**
   * Pedido só no "Iniciar aqui" (etapas assumidas): no card a etapa já mostra o valor. Ex.: a rota, que o card
   * deriva dos tipos marcados e que, começando no meio, só dá para informar assim.
   */
  soNoIniciarAqui?: boolean;
  /**
   * Máscara de leitura aplicada ao valor digitado.
   *
   * Existe porque o mesmo CNPJ aparecia DUAS vezes na mesma tela em formatos
   * diferentes: `68.009.352/0001-41` no cabeçalho (que já passava por
   * `formatarCnpj`) e `68009352000141` cru no campo logo abaixo. A operação leu
   * como bagunça, e era.
   */
  mascara?: 'cnpj';
}

export interface PlaybookCheck {
  /** estável — persistido em estado_etapas[coluna][key]. */
  key: string;
  label: string;
  /** obrigatório → trava o "Avançar" até marcado. */
  req: boolean;
  /**
   * Portal do órgão (2026-08-06, pedido da Juliana). Quando presente, o item do
   * checklist ganha um link "abrir" em nova aba. Não participa do gate nem da
   * persistência — é só atalho de navegação.
   */
  url?: string;
  /**
   * Só entra quando o regime escolhido no Levantamento é este, ou quando não dá para saber
   * (processo antigo, com texto livre ou vazio): na dúvida, cobra, e quem souber dispensa.
   */
  regime?: RegimeTributario;
  /**
   * Sai quando o regime escolhido é este: MEI não tem contrato social (16/10), os itens de contrato não se aplicam;
   * nem passa pelo MAT (19/10).
   */
  foraDoRegime?: RegimeTributario;
  /** Só entra com este regime escolhido; regime desconhecido não conta (ex.: o CCMEI, só do MEI). */
  soNoRegime?: RegimeTributario;
  /** Só existe com DBE: some na constituição onde o registry tem `aberturaSemDbe` (Balcão Único). */
  soComDbe?: boolean;
}

export interface PlaybookAcao {
  label: string;
  nota: string;
  /** tipo_operacao do wizard (deep-link). null = gerador não integrado (distrato/baixa). */
  tipoOperacao: 'constituicao' | 'alteracao' | null;
}

// Marcadores de FLUXO da etapa (legenda do board, espelham o mockup):
//  - 'doc'  → gera o documento (usa o gerador atual).
//  - 'wait' → espera externa (conta prazo / SLA).
//  - 'auto' → [LEGADO] automação genérica — substituído por rpa_read/rpa_write nos 3 tipos;
//             mantido no tipo por segurança, mas FORA da legenda (nenhuma etapa usa mais).
//  - 'cond' → etapa condicional (só se aplicável).
//  - 'rpa_read'  → ponto de RPA de LEITURA (Fase 2: robô consulta o órgão — polling).
//  - 'rpa_write' → ponto de RPA de ESCRITA (Fase 2: robô submete ao órgão).
export type FluxoMarker = 'doc' | 'wait' | 'auto' | 'cond' | 'rpa_read' | 'rpa_write';

export interface PlaybookEtapa {
  campos?: PlaybookCampo[];
  checklist?: PlaybookCheck[];
  acao?: PlaybookAcao;
  /** marcadores de fluxo exibidos no header da coluna + drawer (ver FLUXO_META). */
  fluxo?: FluxoMarker[];
  /** nota da etapa (espera externa / automação futura / condicional). */
  nota?: string;
  /**
   * Viabilidade COMPOSTA (multi-junta): o drawer renderiza os `viabilidadeSubpassos`
   * do registry (UF, município) no lugar do checklist estático — GO=1 passo,
   * SP-int/RJ-cap=2 passos. Ver registries.ts.
   */
  viabilidadeComposta?: boolean;
  /** os itens do checklist são os `orgaosPosRegistro` do registry (etapa "Demais órgãos"). */
  orgaosDoRegistry?: boolean;
  /** Só existe na Rota A da alteração — a Rota B pula (Avançar, stepper e arraste). */
  somenteRotaA?: boolean;
  /** Só para quem será cliente: "Não" no Deferido pula a etapa, como a Rota B (ver `CHAVE_CLIENTE`). */
  soComCliente?: boolean;
  /**
   * Pergunta "Houve exigência?" antes da resposta do órgão (2026-10-14, pedido da Juliana: ver por que o
   * card fica parado). Ver `CHAVE_EXIGENCIA`.
   */
  exigencia?: boolean;
  /**
   * Atalho para o sistema EXTERNO da etapa (2026-08-06).
   *
   * Só existe onde o operador é obrigado a sair para um sistema **diferente do
   * portal da junta** — hoje, só o Coletor Nacional (RFB) do DBE. As demais
   * etapas (viabilidade, assinatura, protocolo, taxas) acontecem dentro do
   * próprio portal da UF, que já tem link fixo no topo do drawer; repetir ali
   * seria ruído.
   *
   * Levantamento completo (27 colunas) em
   * `analises/2026-08-06-mapa-links-por-etapa-legalizacao` — deliberadamente
   * NÃO implementado: as URLs de junta/prefeitura não estão confirmadas com
   * quem opera, e link errado é pior que link nenhum.
   */
  link?: { label: string; url: string; soComDbe?: boolean };
}

/**
 * Coletor Nacional (RFB) — onde o DBE é transmitido. Rota por operação: o
 * playbook já sabe o tipo do processo, então o link cai no formulário certo em
 * vez de largar o operador no menu. URLs da pesquisa de portais de 2026-08-06.
 */
const COLETOR = 'https://redesimservicos.rfb.gov.br/coletor-nacional/opcoes';
const LINK_COLETOR: Record<TipoLegalizacao, { label: string; url: string }> = {
  constituicao: { label: 'Coletor Nacional · DBE', url: `${COLETOR}/inscricao-matriz` },
  alteracao: { label: 'Coletor Nacional · DBE', url: `${COLETOR}/alteracao` },
  baixa: { label: 'Coletor Nacional · DBE de baixa', url: `${COLETOR}/baixa` },
};

export type PlaybookTipo = Record<string, PlaybookEtapa>; // coluna → etapa

/**
 * "Simples Nacional deferido?" no Deferido da constituição (19/10, Camila). Resposta em TEXTO ('Sim' | 'Não'), como
 * a da exigência: a aba Atividade do back trata `false` como ausente. "Não" pede o motivo (o que a Receita apontou).
 */
export const CHAVE_SIMPLES = 'simples_deferido';
export const CHAVE_SIMPLES_MOTIVO = 'simples_motivo';
export const RESPOSTAS_SIMPLES = ['Sim', 'Não'] as const;

/**
 * "Será cliente da Especialistas?" no Deferido da constituição (27/10, Juliana, TI-2026-0346). Resposta em texto
 * ('Sim' | 'Não'), como a do Simples; "Não" pula "Demais órgãos" e o Licenciamento (user, 27/10).
 */
export const CHAVE_CLIENTE = 'sera_cliente';

/** Respondeu "Não" ao `CHAVE_CLIENTE`? Varre os blocos, como `tiposAlteracaoDoProcesso`. */
export function naoSeraCliente(estadoEtapas: Record<string, unknown> | null | undefined): boolean {
  return Object.values(estadoEtapas ?? {}).some(
    b => (b as Record<string, unknown> | null | undefined)?.[CHAVE_CLIENTE] === 'Não',
  );
}

/** Item da Definição + Alteração que sai dos tipos marcados (19/10, Camila): ver `itemSatisfeito`. */
export const CHAVE_ROTA_DEFINIDA = 'rota_definida';

/** Motivo do indeferimento do Simples ('' se nada). */
export function motivoDoSimples(estado: Record<string, unknown> | null | undefined): string {
  const v = (estado ?? {})[CHAVE_SIMPLES_MOTIVO];
  return typeof v === 'string' ? v.trim() : '';
}

/**
 * Licenciamento (alvará e licenças por atividade): etapa própria na alteração (Rota A, 2026-09-11) e na constituição
 * (19/10, Camila: "precisa do licenciamento"; coluna criada pela 0297 do back). Itens em rascunho aprovado pelo user —
 * ajustar ACRESCENTANDO item, nunca renomeando a key (é o que fica salvo no card).
 */
const NOTA_LICENCIAMENTO =
  'Licença de funcionamento (alvará) na prefeitura e, quando a atividade exigir, Bombeiros, Vigilância Sanitária e licença ambiental.';
const CAMPOS_LICENCIAMENTO: PlaybookCampo[] = [
  {
    key: 'lic_grau_risco',
    label: 'Grau de risco da atividade',
    origem: 'estado',
    placeholder: 'baixo / médio / alto',
  },
  {
    key: 'lic_protocolo',
    label: 'Protocolo do licenciamento',
    origem: 'estado',
    placeholder: 'a preencher',
    marca: 'lic_solicitado',
  },
  {
    key: 'lic_numero',
    label: 'Nº do alvará / licença',
    origem: 'estado',
    placeholder: 'sai na emissão',
    marca: 'lic_emitida',
  },
  { key: 'lic_validade', label: 'Validade da licença', origem: 'estado', tipo: 'date' },
];
const CHECKLIST_LICENCIAMENTO: PlaybookCheck[] = [
  { key: 'lic_solicitado', label: 'Licenciamento solicitado (prefeitura)', req: true },
  { key: 'lic_bombeiros', label: 'Bombeiros — se a atividade exigir', req: false },
  { key: 'lic_vigilancia', label: 'Vigilância Sanitária — se a atividade exigir', req: false },
  { key: 'lic_ambiental', label: 'Licença ambiental — se a atividade exigir', req: false },
  { key: 'lic_emitida', label: 'Alvará / licença emitido', req: true },
];

/** Documentos da prefeitura sobre o imóvel (Levantamento e Definição + Alteração). */
const ITENS_DO_IMOVEL: PlaybookCheck[] = [
  { key: 'uso_solo', label: 'Uso do solo (documento da prefeitura)', req: false },
  {
    key: 'numeracao_predial',
    label: 'Numeração predial oficial (documento da prefeitura)',
    req: false,
  },
];

/** Exigência da junta: prazo federal, vale em todo estado. */
const NOTA_EXIGENCIA =
  'Exigência: cumprir em até 30 dias da ciência; depois disso vira novo pedido, com nova guia (Lei 8.934, art. 40).';

/** O número do processo é o do início; só muda se houver novo protocolo (Camila, 19/10). */
const CAMPO_NOVO_PROTOCOLO: PlaybookCampo = {
  key: 'protocolo_numero',
  label: 'Novo protocolo (só se mudou)',
  origem: 'subtype',
  placeholder: 'em branco = o mesmo do início',
  marca: 'protocolado',
};

export const PLAYBOOK: Record<TipoLegalizacao, PlaybookTipo> = {
  constituicao: {
    // 2026-08-11: fusão de "Levantamento" + "Contrato". A geração subiu pra primeira
    // coluna — gera-se o contrato e manda pro cliente aprovar ANTES de o processo
    // formal começar. O levantamento coleta exatamente o que o wizard consome
    // (sócios, empresa, imóvel, regime), então as duas etapas viraram uma só.
    // ⚠️ A VIABILIDADE, que aprova o nome na Junta, agora vem DEPOIS da geração:
    // nome reprovado ⇒ regerar. É consequência aceita do fluxo pedido.
    'Levantamento + Contrato': {
      fluxo: ['doc'],
      nota: 'Regime: o Simples Nacional é pedido no MAT, junto da inscrição do CNPJ, logo depois do registro na junta (etapa "Deferido · NIRE + CNPJ"); o MAT é assinado pelo representante perante o CNPJ e pelo contador indicado (quando houver), com gov.br prata ou ouro, e só o representante pede o Simples. MEI não passa pelo MAT: fica no Portal do Empreendedor. Lucro Presumido e Lucro Real não passam pelo MAT: a opção é o pagamento da 1ª quota do IRPJ (DARF).',
      acao: {
        label: 'Gerar contrato social',
        nota: 'Abre o gerador numa aba nova, já em constituição de SOCIEDADE (LTDA/SLU). O gerador não lê este card: sócios e empresa são informados lá. Ao gerar, o contrato fica vinculado ao card (botão "Abrir contrato"). Empresário Individual (Requerimento de Empresário) não é gerado aqui — instrumento feito fora do sistema.',
        tipoOperacao: 'constituicao',
      },
      campos: [
        {
          key: 'socios',
          label: 'Sócios',
          origem: 'display',
          valorDisplay: 'Coletados no wizard (OCR CNH)',
        },
        {
          key: 'empresa',
          label: 'Empresa',
          origem: 'display',
          valorDisplay: 'Razão / fantasia · CNAE · capital · porte',
        },
        // Societário (20/10): próprio/alugado e zona não servem para nada; o portal pergunta o tipo de unidade e a forma
        // de atuação (definições da RFB). As keys antigas ficam no banco, sem aparecer.
        {
          key: 'tipo_unidade',
          label: 'Tipo de unidade',
          origem: 'estado',
          tipo: 'select',
          opcoes: ['Produtiva', 'Auxiliar'],
          placeholder: 'Selecione',
        },
        {
          key: 'formas_atuacao',
          label: 'Forma(s) de atuação (unidade produtiva)',
          origem: 'estado',
          placeholder: 'ex.: estabelecimento fixo, internet',
        },
        {
          key: 'imovel_area',
          label: 'Área total / ocupada (m²)',
          origem: 'estado',
          placeholder: 'ex.: 120 / 80',
        },
        {
          key: 'imovel_iptu',
          label: 'Inscrição imobiliária (IPTU)',
          origem: 'estado',
          placeholder: 'a preencher',
        },
        {
          key: 'regime_pretendido',
          label: 'Regime tributário pretendido',
          origem: 'estado',
          tipo: 'select',
          opcoes: REGIMES_TRIBUTARIOS,
          placeholder: 'Selecione',
          marca: 'regime_ok',
        },
        {
          key: 'email_empresa',
          label: 'E-mail da empresa',
          origem: 'estado',
          placeholder: 'vai no cadastro da empresa',
        },
        {
          key: 'telefone_empresa',
          label: 'Telefone da empresa',
          origem: 'estado',
          placeholder: 'vai no cadastro da empresa',
        },
      ],
      checklist: [
        { key: 'socios_ok', label: 'Dados dos sócios completos', req: true },
        { key: 'empresa_ok', label: 'Empresa: razão / CNAE / capital / porte', req: true },
        { key: 'imovel_ok', label: 'Imóvel + IPTU + área', req: true },
        // 19/10 (Camila): documentos da prefeitura que o alvará pede (Goiânia: "Documento do Uso do Solo" e
        // "Documento de Numeração Predial Oficial"). Rastreio: não travam, o arquivo vai nos anexos.
        ...ITENS_DO_IMOVEL,
        { key: 'regime_ok', label: 'Regime pretendido definido', req: true },
        // vinha da etapa "Contrato" — derivado do contrato vinculado (não é manual
        // quando há contrato; vira checkbox do operador no fluxo do Empresário
        // Individual, cujo Requerimento é feito fora do sistema).
        { key: 'contrato_gerado', label: 'Contrato gerado (docx)', req: true, foraDoRegime: 'MEI' },
        // Aceite do cliente ANTES de o processo formal começar — é o gate que fecha o
        // fluxo pedido pela analista ("gerar contrato, cliente aprovou do jeito que
        // ficou? começa o processo"). Marca MANUAL (controle interno), mas VERSIONADA:
        // vale para o documento que o cliente viu. Regerar com conteúdo diferente a
        // invalida — ver CHAVE_APROVACAO_HASH no drawer.
        {
          key: 'cliente_aprovou',
          label: 'Cliente aprovou o documento gerado',
          req: true,
          foraDoRegime: 'MEI',
        },
      ],
    },
    Viabilidade: {
      fluxo: ['wait', 'rpa_write', 'rpa_read'],
      viabilidadeComposta: true,
      exigencia: true,
      nota: 'Análise em até 2 dias úteis (CGSIM). Quem segue sem esperar a análise da prefeitura: se ela indeferir, o alvará não sai. Fase 2: o robô de acompanhamento (polling) marca estes itens e avança o card sozinho.',
      campos: [
        {
          key: 'gop',
          label: 'Protocolo de viabilidade',
          rotuloRegistry: 'abertura',
          origem: 'subtype',
          placeholder: 'ex.: 20260703-4471',
        },
        { key: 'viab_data_envio', label: 'Data de envio', origem: 'estado', tipo: 'date' },
      ],
      checklist: [
        { key: 'nome_aprovado', label: 'Nome aprovado (junta comercial)', req: true },
        { key: 'endereco_aprovado', label: 'Endereço aprovado (prefeitura)', req: true },
      ],
    },
    // Constituição NÃO coleta `gon` (protocolo de registro) como a alteração: na abertura há
    // SEMPRE viabilidade, então o protocolo REDESIM é o próprio GOP (letra P), já capturado na
    // Viabilidade. O `gon` (letra N) só existe na alteração Rota B (sem viabilidade). O protocolo
    // de ARQUIVAMENTO da junta é outro número (capturado em "Protocolado"). Não reintroduzir `gon`.
    // 19/10: onde a abertura é pelo Balcão Único (registry `aberturaSemDbe`, hoje GO), o DBE e o Coletor somem.
    'Coleta RFB + Junta': {
      link: { ...LINK_COLETOR.constituicao, soComDbe: true },
      fluxo: ['rpa_write', 'rpa_read'],
      campos: [
        {
          key: 'dbe_recibo',
          label: 'DBE — recibo',
          origem: 'estado',
          placeholder: 'a preencher',
          soComDbe: true,
        },
        {
          key: 'dbe_identificacao',
          label: 'DBE — identificação',
          origem: 'estado',
          placeholder: 'a preencher',
          soComDbe: true,
        },
        {
          key: 'dare_valor',
          label: 'Guia — valor (R$)',
          rotuloRegistry: 'guia',
          origem: 'subtype',
          tipo: 'number',
          placeholder: 'ex.: 176.00',
        },
      ],
      // 19/10: sai "Guia gerada" (o portal só protocola com a guia compensada: um item basta) e o "(RE p/ EI)" do
      // rótulo (em GO o RE virou contrato em 2019; o EI também preenche a FCN). Keys iguais.
      checklist: [
        { key: 'dbe_transmitido', label: 'DBE transmitido', req: true, soComDbe: true },
        { key: 'fcn_preenchida', label: 'FCN preenchida', req: true },
        { key: 'dare_paga', label: 'Guia paga e compensada ({guia})', req: true },
      ],
    },
    Assinatura: {
      nota: 'Visto de advogado só quando a empresa não é ME nem EPP (LC 123, art. 9º, § 2º).',
      checklist: [
        { key: 'docs_anexados', label: 'Todos os documentos anexados', req: true },
        { key: 'socios_assinaram', label: 'Assinado por todos os sócios', req: true },
      ],
    },
    Protocolado: {
      fluxo: ['wait', 'rpa_write', 'rpa_read'],
      exigencia: true,
      nota: `Aguardando deferimento da junta. ${NOTA_EXIGENCIA} Fase 2: acompanhamento por protocolo auto-avança.`,
      campos: [
        CAMPO_NOVO_PROTOCOLO,
        { key: 'protocolo_data', label: 'Protocolo — data', origem: 'subtype', tipo: 'date' },
      ],
      checklist: [
        { key: 'protocolado', label: 'Protocolado na junta', req: true },
        { key: 'deferido', label: 'Deferido', req: true },
      ],
    },
    'Deferido · NIRE + CNPJ': {
      fluxo: ['rpa_read', 'rpa_write'],
      nota: 'O CNPJ sai pelo MAT: registrado o ato na junta, o botão aparece no Acompanhamento do Protocolo Redesim (90 dias, senão a solicitação cancela sozinha). Assinam o representante perante o CNPJ e o contador indicado (quando houver), com gov.br prata ou ouro; não aceita procuração eletrônica. Simples Nacional: pedir ali, junto do CNPJ (só o representante pede); vale desde a inscrição e, se indeferido, há 30 dias para regularizar. Perdeu: só na janela anual do Portal do Simples, sem retroagir.',
      campos: [
        {
          key: 'nire',
          label: 'NIRE',
          origem: 'subtype',
          placeholder: 'sai no deferimento',
          marca: 'nire_emitido',
        },
        {
          key: 'cnpj',
          mascara: 'cnpj',
          label: 'CNPJ',
          origem: 'subtype',
          placeholder: 'sai no deferimento',
          marca: 'cnpj_emitido',
        },
      ],
      // `contrato_emitido` (2026-08-28, pedido da Juliana): o documento REGISTRADO que a
      // junta devolve depois de deferir. Antes a coluna só guardava os números que
      // saíram (NIRE/CNPJ) e nada dizia que o contrato em si já estava em mãos.
      //
      // `req: true` porque a operação classificou como etapa, não rastreio. Consequência
      // a avisar à equipe: card PARADO nesta coluna passa a ter o "Avançar" fechado até
      // alguém marcar — o gate conta os obrigatórios da coluna ATUAL. Quem não tiver o
      // documento por um motivo legítimo dispensa com justificativa.
      //
      // FORA de `CHECKS_DO_ORGAO` de propósito, seguindo `nire_emitido`/`cnpj_emitido`:
      // emitir é da junta, mas BUSCAR e registrar é da equipe.
      //
      // `simples_mat` (2026-10-02): a opção pelo Simples saiu do Entregue, onde já era tarde.
      // Desde 01/12/2025 a empresa nova pede o Simples no MAT, simultâneo à inscrição do CNPJ
      // (Res. CGSN 183/2025, art. 6º, § 5º); o regime é escolhido no Levantamento.
      // Sai com MEI (19/10): o MAT "não altera os procedimentos do MEI, que permanecem
      // centralizados no Portal do Empreendedor" (FAQ do MAT da RFB, 1.1).
      checklist: [
        { key: 'nire_emitido', label: 'NIRE emitido', req: true },
        // 19/10 (Camila): no lugar de "Simples Nacional pedido no MAT" (`simples_mat`, órfã), a resposta da
        // Receita: Sim, ou Não com o motivo. Ver `CHAVE_SIMPLES`.
        {
          key: CHAVE_SIMPLES,
          label: 'Simples Nacional deferido?',
          req: true,
          regime: 'Simples Nacional',
          foraDoRegime: 'MEI',
        },
        { key: 'cnpj_emitido', label: 'CNPJ emitido', req: true },
        { key: 'contrato_emitido', label: 'Contrato emitido', req: true, foraDoRegime: 'MEI' },
        // 19/10 (Camila): o MEI não tem contrato; o documento dele é o CCMEI.
        { key: 'ccmei_emitido', label: 'CCMEI emitido', req: true, soNoRegime: 'MEI' },
        { key: CHAVE_CLIENTE, label: 'Será cliente da Especialistas?', req: true },
      ],
    },
    'Demais órgãos': {
      soComCliente: true,
      fluxo: ['cond', 'rpa_read', 'rpa_write'],
      orgaosDoRegistry: true,
      nota: 'Checklist gerado por atividade (CNAE) — só aparecem os aplicáveis. Variam por estado/município. Em municípios integrados, IE e Inscrição Municipal costumam sair JUNTO do deferimento (etapa anterior) — na Fase 2 o robô de leitura marca sozinho; hoje, confira e marque.',
      // Fallback (UF sem registry). Enxugado em 2026-08-06 junto com
      // `orgaosPosRegistro` — órgão do estado + Prefeitura. Ver registries.ts.
      checklist: [
        { key: 'ie_sefaz', label: 'Inscrição Estadual (SEFAZ)', req: false },
        { key: 'im_alvara', label: 'Inscrição Municipal + Alvará', req: false },
      ],
    },
    // 19/10: o mesmo licenciamento da alteração, sem rota (ver `CAMPOS_LICENCIAMENTO`).
    Licenciamento: {
      soComCliente: true,
      fluxo: ['wait', 'cond'],
      nota: NOTA_LICENCIAMENTO,
      campos: CAMPOS_LICENCIAMENTO,
      checklist: CHECKLIST_LICENCIAMENTO,
    },
    Entregue: {
      fluxo: ['rpa_read', 'rpa_write'],
      nota: 'NFS-e: ME e EPP do Simples emitem só pelo Emissor Nacional desde 1/11/2026 (Resolução CGSN 191/2026).',
      // e-CNPJ (19/10): quem emite é outro departamento; aqui fica o pedido. MEI é dispensado (CGSN 140, art. 110).
      checklist: [
        { key: 'ecnpj', label: 'e-CNPJ pedido ao responsável', req: true, foraDoRegime: 'MEI' },
        { key: 'nf', label: 'Nota fiscal configurada', req: false },
        { key: 'dossie', label: 'Dossiê entregue ao cliente', req: true },
      ],
    },
  },

  alteracao: {
    // 2026-08-11: fusão de "Definição + Rota" + "Alteração + Consolidação". Mesma
    // razão da constituição: gera-se o documento e manda pro cliente aprovar antes de
    // o processo formal começar. Aqui a fusão é ainda mais natural — o documento sai dos
    // mesmos `tipos_alteracao` definidos nesta etapa (marcados de novo no wizard: ele não
    // lê o card).
    'Definição + Alteração': {
      fluxo: ['doc'],
      acao: {
        label: 'Gerar alteração consolidada',
        nota: 'Abre o gerador numa aba nova, já em alteração — marque lá os mesmos tipos de alteração desta etapa (o gerador não lê o card). Ao gerar, o contrato fica vinculado ao card; a vistoria de fidelidade, na listagem de contratos, precisa ser aprovada para o card avançar.',
        tipoOperacao: 'alteracao',
      },
      nota: 'A rota sai dos tipos de alteração (A: endereço/razão/objeto/abertura ou mudança de endereço de filial · B: capital/quadro/administração/extinção de filial; misto A+B → A), e "Rota definida" marca sozinha. "Cadastro na junta bate?": o portal só deixa seguir se o cadastro da junta bate com o último ato arquivado; se não bate, "Solicitar Atualização de Matriz" (até 3 dias úteis), só para corrigir o que já está arquivado.',
      campos: [
        { key: 'tipos_alteracao', label: 'O que está sendo alterado', origem: 'tipos' },
        // No card a rota aparece embaixo dos tipos ("→ Rota A: ..."), e o salvar grava a dos tipos: a caixa só
        // existe no "Iniciar aqui", onde não há tipos para marcar (19/10).
        {
          key: 'rota',
          label: 'Rota derivada',
          origem: 'subtype',
          tipo: 'text',
          placeholder: 'marque os tipos p/ derivar',
          soNoIniciarAqui: true,
        },
      ],
      checklist: [
        // Marcada pelo card a partir dos tipos (19/10, Camila): ver `CHAVE_ROTA_DEFINIDA` no drawer.
        { key: 'rota_definida', label: 'Rota definida (A/B)', req: true },
        { key: 'cadastro_bate', label: 'Cadastro na junta bate com o último contrato', req: true },
        ...ITENS_DO_IMOVEL,
        // vinham da etapa "Alteração + Consolidação" — derivados do contrato vinculado.
        { key: 'doc_gerado', label: 'Documento gerado (docx)', req: true },
        { key: 'auditoria_ok', label: 'Auditoria de fidelidade aprovada', req: true },
        // Aceite do cliente — ver a nota gêmea na constituição. Marca manual e
        // VERSIONADA pelo hash do docx: regerar com conteúdo diferente a invalida.
        { key: 'cliente_aprovou', label: 'Cliente aprovou o documento gerado', req: true },
      ],
    },
    'Viabilidade (Rota A)': {
      fluxo: ['wait', 'rpa_write', 'rpa_read', 'cond'],
      viabilidadeComposta: true,
      somenteRotaA: true,
      exigencia: true,
      nota: 'Só Rota A. Rota B pula esta etapa (gera o protocolo REDESIM direto). Análise em até 2 dias úteis (CGSIM).',
      campos: [
        {
          key: 'gop',
          label: 'Protocolo de viabilidade',
          rotuloRegistry: 'viabilidade',
          origem: 'subtype',
          placeholder: 'ex.: 20260702-7742',
        },
      ],
      checklist: [{ key: 'nome_end_aprovado', label: 'Nome/endereço aprovado', req: true }],
    },
    'Coleta RFB + Junta': {
      link: LINK_COLETOR.alteracao,
      fluxo: ['rpa_write', 'rpa_read'],
      campos: [
        {
          key: 'gon',
          label: 'Protocolo de registro',
          rotuloRegistry: 'registro',
          origem: 'subtype',
          placeholder: 'ex.: REDESIM 8841-2026',
        },
        {
          key: 'dare_valor',
          label: 'Guia — valor (R$)',
          rotuloRegistry: 'guia',
          origem: 'subtype',
          tipo: 'number',
          placeholder: 'ex.: 176.00',
        },
        // E-mail e telefone saíram (19/10, Camila): são alterados à parte, com o certificado digital.
      ],
      checklist: [
        { key: 'dbe_transmitido', label: 'DBE transmitido', req: true },
        { key: 'fcn_preenchida', label: 'FCN preenchida', req: true },
        { key: 'dare_paga', label: 'Guia paga ({guia})', req: true },
      ],
    },
    Assinatura: {
      checklist: [
        { key: 'docs_anexados', label: 'Documentos anexados', req: true },
        { key: 'todos_assinaram', label: 'Assinado por todos os sócios', req: true },
      ],
    },
    Protocolado: {
      fluxo: ['wait', 'rpa_write', 'rpa_read'],
      exigencia: true,
      nota: `Aguardando deferimento. ${NOTA_EXIGENCIA}`,
      campos: [CAMPO_NOVO_PROTOCOLO],
      checklist: [
        { key: 'protocolado', label: 'Protocolado na junta', req: true },
        { key: 'deferido', label: 'Deferido', req: true },
      ],
    },
    'Deferido · CNPJ atualizado': {
      fluxo: ['rpa_read'],
      campos: [
        {
          key: 'cnpj',
          mascara: 'cnpj',
          label: 'CNPJ',
          origem: 'subtype',
          placeholder: 'atualiza automático',
          marca: 'cnpj_atualizado',
        },
      ],
      // `alteracao_emitida` — mesmo pedido da `contrato_emitido` da constituição
      // (2026-08-28): o ato REGISTRADO que volta da junta. Ver a nota lá para o porquê
      // do `req: true` e da ausência em `CHECKS_DO_ORGAO`.
      checklist: [
        { key: 'cnpj_atualizado', label: 'CNPJ atualizado', req: true },
        { key: 'alteracao_emitida', label: 'Alteração emitida', req: true },
      ],
    },
    'Demais órgãos': {
      fluxo: ['cond', 'rpa_read', 'rpa_write'],
      orgaosDoRegistry: true,
      nota: 'Só se mudou endereço ou atividade.',
      checklist: [
        { key: 'orgaos_atualizados', label: 'Órgãos aplicáveis atualizados', req: false },
      ],
    },
    // 2026-09-11: licenciamento é processo próprio na Rota A. Itens em rascunho aprovado pelo
    // user — ajustar ACRESCENTANDO item, nunca renomeando a key (é o que fica salvo no card).
    'Licenciamento (Rota A)': {
      fluxo: ['wait', 'cond'],
      somenteRotaA: true,
      nota: `Só Rota A. ${NOTA_LICENCIAMENTO} A Rota B pula esta etapa.`,
      campos: CAMPOS_LICENCIAMENTO,
      checklist: CHECKLIST_LICENCIAMENTO,
    },
    Entregue: {
      checklist: [{ key: 'comprovantes', label: 'Comprovantes entregues ao cliente', req: true }],
    },
  },

  baixa: {
    // Camila (09/09): declarações de extinção e eSocial são dos departamentos, não do
    // paralegal — na baixa ele consulta cada gestor. Keys antigas ficam órfãs (inofensivo).
    // Camila (20/10): a consulta aos gestores, que era a etapa "Declarações finais", fica aqui
    // (a coluna saiu na 0297 do back), e "Débitos quitados" virou obrigatório. A lei não exige:
    // a baixa com débito é dispensa do item, com o motivo.
    'Regularização + Certidões': {
      fluxo: ['wait', 'rpa_read'],
      nota: 'Pergunte ao gestor de cada departamento (fiscal, contábil, pessoal) se há débito, declaração ou outra pendência em aberto; as declarações de extinção e o eSocial ficam com os departamentos. A lei não exige a quitação: a baixa ocorre INDEPENDENTE de regularidade fiscal/previdenciária/trabalhista (LC 147/2014, IN RFB 2.119/2022, STJ) e os débitos migram aos sócios. O escritório só segue com os débitos quitados; se o cliente decidir baixar com débito, dispense o item com o motivo. As certidões são RASTREIO (recomendável emitir p/ proteger os sócios), NÃO travam. Fase 2: robô emite as certidões (reusa a stack de scraping).',
      checklist: [
        {
          key: 'ciente_baixa_independe',
          label: 'Ciente: a baixa independe de débitos; pendências migram aos sócios',
          req: true,
        },
        { key: 'debitos_quitados', label: 'Débitos quitados', req: true },
        {
          key: 'gestores_consultados',
          label:
            'Gestores dos departamentos consultados sobre débitos, declarações ou pendências em aberto',
          req: true,
        },
        {
          key: 'cert_federal',
          label: 'Certidão Federal / PGFN (unificada — inclui INSS)',
          req: false,
        },
        { key: 'cert_sefaz', label: 'SEFAZ (estadual) — se teve IE', req: false },
        { key: 'cert_municipal', label: 'Municipal — se teve IM', req: false },
        { key: 'cert_fgts', label: 'FGTS — se teve empregados', req: false },
        { key: 'cert_cndt', label: 'CNDT (trabalhista) — recomendável', req: false },
      ],
    },
    Distrato: {
      fluxo: ['doc'],
      acao: {
        // Sem gerador: o card mostra o rótulo como aviso, não como botão.
        // Societário (20/10): o escritório usa o distrato do portal, não tem modelo próprio.
        label: 'Distrato — gerado no portal da junta',
        nota: 'Distrato social (sociedade) ou requerimento de baixa (Empresário Individual): gerado no portal da junta, o escritório não usa modelo próprio. Marque quando estiver pronto e assinado.',
        tipoOperacao: null,
      },
      campos: [
        {
          key: 'guarda_livros',
          label: 'Sócio responsável pela guarda dos livros',
          origem: 'estado',
          placeholder: 'a definir',
        },
      ],
      checklist: [
        { key: 'ato_gerado', label: 'Distrato gerado no portal', req: true },
        { key: 'partilha_definida', label: 'Partilha do acervo definida', req: true },
      ],
    },
    'DBE de baixa + Portal': {
      link: LINK_COLETOR.baixa,
      fluxo: ['rpa_write'],
      nota: 'A RFB INDEFERE a baixa se o QSA do CNPJ divergir do ato de extinção — confira/atualize o quadro na base do CNPJ antes de transmitir o DBE.',
      campos: [
        {
          key: 'gon',
          label: 'DBE de baixa (REDESIM)',
          origem: 'subtype',
          placeholder: 'a preencher',
        },
      ],
      checklist: [
        { key: 'qsa_confere', label: 'QSA do CNPJ bate com o ato de extinção', req: true },
        { key: 'dbe_baixa', label: 'DBE de baixa transmitido', req: true },
        { key: 'portal_iniciado', label: 'Iniciado no portal (NIRE+CNPJ)', req: true },
        // 19/10: a baixa também usa a FCN (manual de baixa JUCEG 2026); a key ficou a de antes.
        { key: 're_preenchido', label: 'FCN preenchida', req: true },
      ],
    },
    'Taxas + Assinatura': {
      nota: 'Extinção é ISENTA de emolumentos (art. 55 §2º Lei 8.934/94 — federal, todas as juntas) e a baixa na RFB (DBE) é gratuita. Guia SÓ se o processo empacotar atos acessórios (ex.: uma alteração antes do distrato).',
      campos: [
        {
          key: 'dare_valor',
          label: 'Guia — valor (R$) — só se houver ato acessório',
          rotuloRegistry: 'guia',
          origem: 'subtype',
          tipo: 'number',
          placeholder: 'isento (extinção)',
        },
      ],
      checklist: [
        { key: 'dare_paga', label: 'Guia paga (só se houver ato acessório)', req: false },
        { key: 'assinado', label: 'Assinado', req: true },
      ],
    },
    Protocolado: {
      fluxo: ['wait', 'rpa_write', 'rpa_read'],
      exigencia: true,
      // 19/10: era "até 5 dias úteis (IN DREI 81/2020)". Distrato de LTDA e extinção de EI são decisão
      // singular: 2 dias úteis (Lei 8.934, art. 42, § 2º, redação da Lei 13.874/2019).
      nota: `Aguardando deferimento. Análise em até 2 dias úteis (decisão singular, Lei 8.934, art. 42, § 2º). ${NOTA_EXIGENCIA}`,
      // Mesma coluna `protocolo_numero` da constituição e da alteração (a tabela é uma só).
      campos: [CAMPO_NOVO_PROTOCOLO],
      checklist: [
        { key: 'protocolado', label: 'Protocolado na junta', req: true },
        { key: 'deferido', label: 'Deferido', req: true },
      ],
    },
    'Deferido · CNPJ baixado': {
      fluxo: ['rpa_read'],
      campos: [
        {
          key: 'cnpj',
          mascara: 'cnpj',
          label: 'CNPJ',
          origem: 'subtype',
          placeholder: 'baixado',
          marca: 'cnpj_baixado',
        },
      ],
      checklist: [
        { key: 'cnpj_baixado', label: 'CNPJ baixado', req: true },
        { key: 'certidao_baixa', label: 'Certidão de Baixa do CNPJ emitida (RFB)', req: false },
      ],
    },
    'Baixar inscrições': {
      fluxo: ['cond', 'rpa_read', 'rpa_write'],
      nota: 'IE cai AUTOMÁTICA na baixa via REDESIM (GO/SP; RJ cascateia no REGIN) — só confirmar. IM depende do município: muitas prefeituras NÃO integram → cancelar o CCM/inscrição à parte. Avise o fiscal com a data da baixa: a declaração de extinção do Simples depende dela (até 31/12/2026, DEFIS de extinção; a partir de 2027, no PGDAS-D do mês do evento, Resolução CGSN 190/2026).',
      checklist: [
        {
          key: 'ie_sefaz',
          label: 'Inscrição Estadual — cai automático via REDESIM (confirmar)',
          req: false,
        },
        { key: 'im', label: 'Inscrição Municipal — condicional por município', req: false },
        // Societário (20/10): o card lembra; aqui, e não no Deferido, porque só depois do deferimento há a data.
        {
          key: 'fiscal_avisado',
          label: 'Fiscal avisado da data da baixa (declarações de extinção)',
          req: true,
        },
      ],
    },
    // Societário (20/10): o responsável pelos livros é o sócio, já pedido no Distrato.
    'Arquivado (guarda 5 anos)': {
      nota: 'Os livros ficam com o sócio indicado no Distrato. O escritório guarda a documentação da baixa por 5 anos.',
      checklist: [{ key: 'doc_arquivada', label: 'Documentação arquivada', req: true }],
    },
  },
};

/** Etapa (bloco) do playbook para o tipo + coluna atual. */
export function etapaDoPlaybook(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
): PlaybookEtapa | undefined {
  if (!coluna) return undefined;
  return PLAYBOOK[tipo]?.[coluna];
}

/**
 * Títulos das etapas do tipo, na ordem — a estrutura do quadro segundo o FRONT.
 *
 * Existe pro caso em que o quadro AINDA NÃO EXISTE no backend: o projeto `lgz_<tipo>` só
 * é criado na primeira legalização daquele tipo (`legalizacao_kanban_sync.ensure_projeto`),
 * então num tipo virgem a tela não tinha coluna nenhuma pra desenhar — e sem coluna não
 * havia "Iniciar aqui", justamente onde ele é mais útil (cadastrar o processo que já
 * chegou no meio do caminho).
 *
 * ⚠️ Só é seguro usar isto como estrutura do quadro porque estas chaves são, por
 * contrato, os MESMOS títulos que o backend cria (`legalizacao_kanban_sync.COLUNAS_*` —
 * ver a nota no topo do PLAYBOOK). Verificado em 2026-08-13: as 27 strings dos três tipos
 * batem byte a byte. Se divergirem, o "Iniciar aqui" do quadro-estrutura miraria uma
 * coluna que o backend não vai criar e o processo nasceria na primeira etapa.
 */
export function colunasDoPlaybook(tipo: TipoLegalizacao): string[] {
  return Object.keys(PLAYBOOK[tipo] ?? {});
}

/**
 * A coluna do board tem bloco correspondente no PLAYBOOK?
 *
 * O título da coluna é o contrato entre os dois repos — o backend cria as colunas a
 * partir de `legalizacao_kanban_sync.COLUNAS_*` e aqui elas são a CHAVE de lookup. As
 * duas listas são duplicadas, e não há teste que as compare: os testes do módulo estão
 * fora do versionamento (`.git/info/exclude`) e o CI do admin não roda teste nenhum.
 *
 * Sem esta função a falha era FAIL-OPEN: título divergente → `etapaDoPlaybook`
 * devolvia `undefined` sem log → checklist vazio → zero obrigatórios pendentes → o
 * "Avançar" ficava LIBERADO numa etapa que nem campos tinha. O comentário do próprio
 * módulo já dizia que "o gate 'Avançar' não pode destravar com checklist vazio".
 *
 * Quem chama trava o avanço quando isto é falso. A direção de falha passa a ser
 * fechada: na dúvida, não deixa passar.
 */
export function etapaConfigurada(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
): boolean {
  return !!coluna && !!PLAYBOOK[tipo]?.[coluna];
}

/* =========================================================================
 * DISPENSA DE ITEM DO CHECKLIST — "não se aplica, e aqui está o porquê"
 * ========================================================================= */

/** Onde as dispensas de uma etapa moram, DENTRO do bloco da coluna.
 *
 * Prefixo `_` pelo mesmo motivo de `_obs` e `_sla`: separa metadado das chaves de
 * checklist, que são booleanos crus lidos por nome. Sem o prefixo, um item chamado
 * `dispensas` colidiria.
 *
 * ⚠️ O PATCH do backend faz merge por chave de TOPO (`estado_etapas`), e o bloco da
 * coluna é substituído inteiro. Quem grava tem que mandar o bloco completo — que é o
 * que o drawer já faz. Nenhuma mudança de backend: `estado_etapas` é dict livre.
 */
export const CHAVE_DISPENSAS = '_dispensas';

export interface DispensaItem {
  /** `key` de MOTIVOS_DISPENSA. */
  motivo: string;
  /** texto livre — obrigatório quando o motivo é `outro`. */
  obs?: string;
  /** ISO date de quando foi dispensado. */
  em?: string;
  /** nome de quem dispensou. */
  por?: string;
}

/**
 * Motivos oferecidos ao operador. GENÉRICOS de propósito.
 *
 * O pedido nasceu da SEFAZ (obrigatória só para quem tem Inscrição Estadual, ou seja
 * atividade sujeita a ICMS — empresa só de serviço não passa por lá), mas a decisão foi
 * que vale para qualquer item: os órgãos de "Demais órgãos", e também obrigatórios de
 * outras etapas. Uma lista com "sem IE" só serviria à SEFAZ e o segundo pedido viria
 * logo em seguida.
 *
 * O texto livre carrega a especificidade; a opção carrega o que dá para agrupar depois.
 */
export const MOTIVOS_DISPENSA: { key: string; label: string; exigeObs?: boolean }[] = [
  { key: 'nao_se_aplica', label: 'Não se aplica a esta empresa' },
  { key: 'ja_regularizado', label: 'Já regularizado — inscrição/registro já ativo' },
  { key: 'sem_mudanca', label: 'Não houve mudança que exija' },
  { key: 'dispensado_orgao', label: 'Dispensado pelo próprio órgão' },
  // O gerador não cobre todo ato — MEI → S.A., por exemplo. Nesses casos o documento
  // EXISTE, só não saiu daqui, e o processo precisa seguir mesmo assim. Ter o motivo
  // próprio (em vez de cair em "outro") também deixa medir depois com que frequência o
  // gerador fica de fora — que é informação de produto, não só de auditoria.
  { key: 'fora_do_sistema', label: 'Documento produzido fora do sistema' },
  { key: 'outro', label: 'Outro motivo', exigeObs: true },
];

export function rotuloDoMotivo(motivo: string | null | undefined): string {
  return MOTIVOS_DISPENSA.find(m => m.key === motivo)?.label ?? 'Dispensado';
}

/** As dispensas gravadas no bloco de uma etapa. Nunca lança — estado é JSON livre. */
export function dispensasDaEtapa(
  estado: Record<string, unknown> | null | undefined,
): Record<string, DispensaItem> {
  const bruto = (estado ?? {})[CHAVE_DISPENSAS];
  if (!bruto || typeof bruto !== 'object' || Array.isArray(bruto)) return {};
  return bruto as Record<string, DispensaItem>;
}

/** A dispensa de UM item, ou `null`. */
export function dispensaDoItem(
  estado: Record<string, unknown> | null | undefined,
  key: string,
): DispensaItem | null {
  const d = dispensasDaEtapa(estado)[key];
  return d && typeof d === 'object' && typeof d.motivo === 'string' ? d : null;
}

/* =========================================================================
 * EXIGÊNCIA — "Houve exigência?" no Protocolado e na Viabilidade (2026-10-14)
 * ========================================================================= */

/**
 * Resposta e texto da exigência, no bloco da etapa. A resposta é TEXTO ('Sim' | 'Não'), não booleano: a
 * aba Atividade do back trata `false` como ausente, e o "Não" não apareceria lá.
 */
export const CHAVE_EXIGENCIA = 'exigencia';
export const CHAVE_EXIGENCIA_TEXTO = 'exigencia_texto';
export const RESPOSTAS_EXIGENCIA = ['Sim', 'Não'] as const;
export type RespostaExigencia = (typeof RESPOSTAS_EXIGENCIA)[number];

/** A pergunta ocupa um item obrigatório do checklist: é por ele que os gates a cobram. */
const ITEM_EXIGENCIA: PlaybookCheck = {
  key: CHAVE_EXIGENCIA,
  label: 'Houve exigência?',
  req: true,
};

/** O que o órgão pediu ('' se nada). */
export function textoDaExigencia(estado: Record<string, unknown> | null | undefined): string {
  const v = (estado ?? {})[CHAVE_EXIGENCIA_TEXTO];
  return typeof v === 'string' ? v.trim() : '';
}

/** O que o "Não" marcou, para o "Sim" desfazer só isso. Metadado (`_`): fica fora da Atividade. */
const CHAVE_MARCADOS_PELO_NAO = '_exigencia_marcou';

/**
 * O estado da etapa depois de responder. "Não" marca as respostas do órgão ("entende que foi deferido",
 * no pedido); trocar por "Sim" desfaz só o que ele marcou — o que já estava marcado (aprovação real da
 * junta, Deferido de exigência cumprida) fica. Item dispensado fica como está, e o texto também: voltar
 * para "Sim" o traz de volta.
 */
export function responderExigencia(
  estado: Record<string, unknown>,
  resposta: RespostaExigencia,
  doOrgao: PlaybookCheck[],
): Record<string, unknown> {
  const novo: Record<string, unknown> = { ...estado, [CHAVE_EXIGENCIA]: resposta };
  // Só respostas do órgão: o marcador é dado gravado, e o "Sim" não pode mexer em outra chave.
  const marcados = Array.isArray(estado[CHAVE_MARCADOS_PELO_NAO])
    ? (estado[CHAVE_MARCADOS_PELO_NAO] as unknown[]).filter((k): k is string =>
        doOrgao.some(c => c.key === k),
      )
    : [];
  delete novo[CHAVE_MARCADOS_PELO_NAO];
  if (resposta === 'Sim') {
    marcados.forEach(k => {
      novo[k] = false;
    });
    return novo;
  }
  const marca = doOrgao
    .filter(c => !estado[c.key] && !dispensaDoItem(estado, c.key))
    .map(c => c.key);
  marca.forEach(k => {
    novo[k] = true;
  });
  const todos = marcados.concat(marca.filter(k => !marcados.includes(k)));
  if (todos.length) novo[CHAVE_MARCADOS_PELO_NAO] = todos;
  return novo;
}

/** Marcar/desmarcar à mão: o item deixa de ser do "Não", e o "Sim" não mexe mais nele. */
export function marcarItem(
  estado: Record<string, unknown>,
  key: string,
  valor: boolean,
): Record<string, unknown> {
  const novo: Record<string, unknown> = { ...estado, [key]: valor };
  const marcados = estado[CHAVE_MARCADOS_PELO_NAO];
  if (!Array.isArray(marcados)) return novo;
  const resto = marcados.filter(k => k !== key);
  if (resto.length) novo[CHAVE_MARCADOS_PELO_NAO] = resto;
  else delete novo[CHAVE_MARCADOS_PELO_NAO];
  return novo;
}

/**
 * O item está satisfeito? Marcado OU dispensado.
 *
 * A pergunta da exigência é a exceção: vale a resposta — "Não", ou "Sim" com o texto (sem ele não há o
 * que mostrar no card, que é o motivo do pedido). Não tem dispensa.
 *
 * ⚠️ EXISTE PRA SER A ÚNICA RESPOSTA. Antes desta função a pergunta "o item está feito?"
 * era `!!estado[key]` escrito à mão em CINCO lugares — `proximaAcao` (que sozinha
 * atende card do quadro, drawer, visão geral e o filtro do índice), o gate do `Avançar`,
 * as bolinhas de progresso do card, o gate do drag-and-drop e a visão só-leitura.
 *
 * Com a dispensa entrando só em alguns deles, o board diria "falta SEFAZ" enquanto o
 * drawer mostra o item dispensado — a divergência exata contra a qual o próprio módulo
 * já avisa ("o rodapé do modal nunca contradiz o card de onde ele foi aberto"). Por isso
 * os cinco passaram a chamar aqui.
 *
 * Dispensar SATISFAZ inclusive item `req: true`. Um obrigatório dispensado que
 * continuasse travando o "Avançar" tornaria a dispensa decorativa: o operador
 * justificaria e seguiria preso.
 */
export function itemSatisfeito(
  estado: Record<string, unknown> | null | undefined,
  key: string,
): boolean {
  const st = estado ?? {};
  if (key === CHAVE_EXIGENCIA)
    return st[key] === 'Não' || (st[key] === 'Sim' && !!textoDaExigencia(st));
  // Simples: respondido ("Não" só vale com o motivo) ou dispensado (processo sem regime conhecido). O item antigo
  // (`simples_mat`, "pedido no MAT") marcado ou dispensado também vale: o processo que já passou por ele não volta a
  // travar, e o histórico dos finalizados não aparece pendente.
  if (key === CHAVE_SIMPLES)
    return (
      st[key] === 'Sim' ||
      (st[key] === 'Não' && !!motivoDoSimples(st)) ||
      !!dispensaDoItem(st, key) ||
      st.simples_mat === true ||
      !!dispensaDoItem(st, 'simples_mat')
    );
  // "Rota definida" sai dos tipos gravados no mesmo bloco (Definição + Alteração), para o quadro e o arraste
  // concordarem com o card sem esperar alguém salvar a etapa. Dispensa antiga continua valendo, como no drawer.
  if (key === CHAVE_ROTA_DEFINIDA)
    return (
      (Array.isArray(st.tipos_alteracao) &&
        !!classificarRotaAlteracao(st.tipos_alteracao as TipoAlteracao[]).rota) ||
      !!dispensaDoItem(st, key)
    );
  return !!st[key] || !!dispensaDoItem(st, key);
}

/**
 * Extrai os `tipos_alteracao` persistidos no processo. Só a etapa "Definição + Rota"
 * grava essa chave dentro do seu bloco em `estado_etapas` (drawer `montarPatch`); aqui
 * VARREMOS os blocos e pegamos a 1ª array `tipos_alteracao` encontrada — imune a
 * divergência de acento/espaço no título da coluna (a key). `null` quando ausente
 * (card "iniciar aqui", processo legado) → o filtro cai em fail-open (mostra tudo).
 */
export function tiposAlteracaoDoProcesso(
  estadoEtapas: Record<string, unknown> | null | undefined,
): TipoAlteracao[] | null {
  if (!estadoEtapas) return null;
  const bloco = Object.values(estadoEtapas).find(b =>
    Array.isArray((b as { tipos_alteracao?: unknown } | null | undefined)?.tipos_alteracao),
  ) as { tipos_alteracao?: unknown } | undefined;
  return bloco ? (bloco.tipos_alteracao as TipoAlteracao[]) : null;
}

/** Rota do processo: a dos tipos marcados; sem tipos (Iniciar aqui), a gravada. */
export function rotaDoProcesso(
  estadoEtapas: Record<string, unknown> | null | undefined,
  rotaGravada?: string | null,
): 'A' | 'B' | null {
  const daSelecao = classificarRotaAlteracao(tiposAlteracaoDoProcesso(estadoEtapas)).rota;
  if (daSelecao) return daSelecao;
  return rotaGravada === 'A' || rotaGravada === 'B' ? rotaGravada : null;
}

/**
 * A etapa não se aplica ao processo? Rota B nas etapas `somenteRotaA` (rota indefinida mostra) e, com
 * `naoCliente` (ver `naoSeraCliente`), as `soComCliente`.
 */
export function etapaPuladaNaRota(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  rota: 'A' | 'B' | null | undefined,
  naoCliente = false,
): boolean {
  const etapa = etapaDoPlaybook(tipo, coluna);
  if (naoCliente && etapa?.soComCliente) return true;
  return tipo === 'alteracao' && rota === 'B' && !!etapa?.somenteRotaA;
}

/** Próxima etapa que se aplica ao processo depois da coluna `idx`. `null` = é a última. */
export function proximaEtapaAplicavel(
  tipo: TipoLegalizacao,
  colunas: string[],
  idx: number,
  rota: 'A' | 'B' | null | undefined,
  naoCliente = false,
): string | null {
  if (idx < 0) return null;
  return colunas.slice(idx + 1).find(c => !etapaPuladaNaRota(tipo, c, rota, naoCliente)) ?? null;
}

export type ResultadoArraste =
  | { ok: true; direcao: 'forward' | 'backward' }
  | { ok: false; motivo: 'pulo' | 'nao_se_aplica' };

/**
 * O arraste de uma coluna pra outra vale? Uma etapa por vez, contando só as que se aplicam
 * — a Rota B vai de "Demais órgãos" direto pra "Entregue". `null` = mesma coluna.
 */
export function arrasteDeEtapa(
  tipo: TipoLegalizacao,
  colunas: string[],
  de: number,
  para: number,
  rota: 'A' | 'B' | null | undefined,
  naoCliente = false,
): ResultadoArraste | null {
  if (de === para || de < 0 || para < 0) return null;
  if (etapaPuladaNaRota(tipo, colunas[para], rota, naoCliente))
    return { ok: false, motivo: 'nao_se_aplica' };
  const aplicaveis = colunas
    .map((_, i) => i)
    .filter(i => i === de || !etapaPuladaNaRota(tipo, colunas[i], rota, naoCliente));
  const delta = aplicaveis.indexOf(para) - aplicaveis.indexOf(de);
  if (Math.abs(delta) >= 2) return { ok: false, motivo: 'pulo' };
  return { ok: true, direcao: delta > 0 ? 'forward' : 'backward' };
}

/**
 * Sub-passos de Viabilidade APLICÁVEIS ao processo. Filtra `registry.viabilidadeSubpassos`
 * por dois escopos:
 *  - `tipos` (por tipo de legalização) — SEMPRE aplicado (ex.: Balcão Único = só constituição).
 *  - `requerAlteracaoDe` (por gatilho) — SÓ em alteração E SÓ quando os `tiposAlteracao` são
 *    conhecidos.
 *
 * DIREÇÃO DE FALHA = fail-open (trava #1): em alteração com tipos indeterminados
 * (`null`/`[]`), NÃO filtra por gatilho — mostra o subpasso. Esconder um pré-requisito
 * legal (ex.: a CPL do RJ numa alteração de endereço real) é pior que exibir passo a mais.
 *
 * Nunca zera (trava #2): se o filtro por gatilho esvaziar, cai no conjunto tipo-escopado —
 * o gate "Avançar" (drawer) não pode destravar com checklist vazio.
 */
export function subpassosAplicaveis(
  registry: RegistryConfig,
  ctx: { tipoLegalizacao: TipoLegalizacao; tiposAlteracao?: TipoAlteracao[] | null },
): ViabilidadeSubpasso[] {
  const { tipoLegalizacao, tiposAlteracao } = ctx;
  const ehAlteracao = tipoLegalizacao === 'alteracao';
  const tiposConhecidos = !!tiposAlteracao && tiposAlteracao.length > 0;

  // Escopo por tipo (sempre).
  const doTipo = registry.viabilidadeSubpassos.filter(
    sp => !sp.tipos || sp.tipos.includes(tipoLegalizacao),
  );

  // Escopo por gatilho: só em alteração com tipos conhecidos; senão fail-open.
  const filtrado = doTipo.filter(sp => {
    if (!ehAlteracao || !tiposConhecidos || !sp.requerAlteracaoDe) return true;
    return sp.requerAlteracaoDe.some(t => tiposAlteracao!.includes(t));
  });

  return filtrado.length > 0 ? filtrado : doTipo;
}

/**
 * True quando, em ALTERAÇÃO, o registry tem subpasso condicionado por gatilho mas os
 * `tiposAlteracao` do processo estão indeterminados → o filtro caiu em fail-open. O
 * drawer usa isto p/ um aviso "revisar" discreto (o operador confirma se o subpasso
 * a mais realmente se aplica).
 */
export function viabilidadeRevisar(
  registry: RegistryConfig | undefined,
  ctx: { tipoLegalizacao: TipoLegalizacao; tiposAlteracao?: TipoAlteracao[] | null },
): boolean {
  if (!registry || ctx.tipoLegalizacao !== 'alteracao') return false;
  const tiposConhecidos = !!ctx.tiposAlteracao && ctx.tiposAlteracao.length > 0;
  if (tiposConhecidos) return false;
  return registry.viabilidadeSubpassos.some(sp => !!sp.requerAlteracaoDe);
}

/**
 * Troca o token `{guia}` pelo nome da guia da UF ("DARE" em GO/SP, "DAE" em MG, "DAM" na
 * BA, "Emolumentos JUCERJA" no RJ...).
 *
 * ⚠️ Motivo de existir: os itens de guia eram as strings fixas "DARE gerada"/"DARE paga".
 * DARE é o termo de GO e SP. Isso não afetava só as UFs sem registry — MENTIA no RJ, que
 * já era configurado: o CAMPO usava `guia.nome` e dizia "Emolumentos JUCERJA" enquanto o
 * checkbox logo abaixo dizia "DARE paga". Dois nomes para o mesmo ato, na mesma tela.
 *
 * A parametrização por `rotuloRegistry` só alcançava CAMPO, nunca checklist — daí o token.
 */
function comTermosDoRegistry(itens: PlaybookCheck[], registry?: RegistryConfig): PlaybookCheck[] {
  if (!itens.some(i => i.label.includes('{guia}'))) return itens;
  const guia = registry?.guia.nome ?? '';
  return itens.map(i => {
    if (!i.label.includes('{guia}')) return i;
    // Sem nome confirmado (UF genérica, ou processo sem UF gravada), o parêntese inteiro
    // sai — "Guia gerada" em vez de "Guia gerada (Guia de emolumentos)". O substantivo
    // fica no rótulo fixo, então a frase continua completa e concordando.
    const label = guia ? i.label.replace('{guia}', guia) : i.label.replace(/\s*\(\{guia\}\)/, '');
    return { ...i, label };
  });
}

/**
 * Checklist EFETIVO da etapa: se a Viabilidade é composta (multi-junta), os itens
 * vêm dos sub-passos APLICÁVEIS do registry (UF, município, tipo, gatilhos da Rota A);
 * senão, o checklist estático do playbook. Usado pelo drawer (gate), pelo board (drag
 * gate) e pelo card (progresso) — pra os 3 concordarem em SP/RJ.
 */
export function checklistEfetivoDaEtapa(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  uf?: string | null,
  municipio?: string | null,
  tiposAlteracao?: TipoAlteracao[] | null,
  regime?: RegimeTributario | null,
): PlaybookCheck[] {
  const etapa = etapaDoPlaybook(tipo, coluna);
  // Etapa que não se aplica à Rota B não cobra nada dela — board, drawer e arraste concordam.
  if (etapaPuladaNaRota(tipo, coluna, classificarRotaAlteracao(tiposAlteracao ?? null).rota))
    return [];
  // Resolvido UMA vez, e não só dentro do ramo do registry: os itens de guia vivem em
  // etapas comuns (Coleta RFB + Junta, Assinatura) e também precisam do termo da UF.
  const registry = resolverRegistry(uf, municipio);
  if (etapa?.viabilidadeComposta || etapa?.orgaosDoRegistry) {
    if (registry) {
      if (etapa.viabilidadeComposta)
        return comExigencia(
          tipo,
          coluna,
          subpassosAplicaveis(registry, { tipoLegalizacao: tipo, tiposAlteracao }).flatMap(
            sp => sp.checklist,
          ),
        );
      // "Demais órgãos": um item por órgão do registry (rastreio, não trava o Avançar).
      // A `key` segue derivada SÓ do nome — é o que está persistido em
      // `estado_etapas` dos cards existentes. Acrescentar `url` não a muda.
      const orgaos = registry.orgaosPosRegistro.filter(o => !o.tipos || o.tipos.includes(tipo));
      return orgaos.map(o => ({
        key: `org_${o.nome
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/gi, '_')
          .toLowerCase()}`,
        label: o.rotulo ?? o.nome,
        req: false,
        url: o.url,
      }));
    }
  }
  // Item de um regime só sai quando o processo escolheu OUTRO; sem regime conhecido, fica. MEI está dentro do Simples
  // (SIMEI): o item do Simples fica, salvo o que declara `foraDoRegime: 'MEI'` (o pedido no MAT). `soNoRegime` é o
  // contrário: só com aquele regime escolhido.
  const doRegime = (etapa?.checklist ?? []).filter(
    i =>
      (!i.regime ||
        !regime ||
        i.regime === regime ||
        (i.regime === 'Simples Nacional' && regime === 'MEI')) &&
      !(regime && i.foraDoRegime === regime) &&
      !(i.soNoRegime && i.soNoRegime !== regime) &&
      !(i.soComDbe && semDbe(tipo, registry)),
  );
  return comExigencia(tipo, coluna, comTermosDoRegistry(doRegime, registry));
}

/** Constituição pelo Balcão Único: a junta manda os dados à Receita e não há DBE (registry `aberturaSemDbe`). */
function semDbe(tipo: TipoLegalizacao, registry?: RegistryConfig): boolean {
  return tipo === 'constituicao' && !!registry?.aberturaSemDbe;
}

/** Campos da etapa para a UF do processo: sem os de DBE onde a abertura não tem DBE. */
export function camposEfetivosDaEtapa(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  uf?: string | null,
  municipio?: string | null,
): PlaybookCampo[] {
  const campos = etapaDoPlaybook(tipo, coluna)?.campos ?? [];
  const registry = resolverRegistry(uf, municipio);
  return campos.filter(c => !(c.soComDbe && semDbe(tipo, registry)));
}

/** Atalho do sistema externo da etapa, ou nada quando ele é o Coletor e a abertura não tem DBE. */
export function linkDaEtapa(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  uf?: string | null,
  municipio?: string | null,
): PlaybookEtapa['link'] {
  const link = etapaDoPlaybook(tipo, coluna)?.link;
  return link?.soComDbe && semDbe(tipo, resolverRegistry(uf, municipio)) ? undefined : link;
}

/**
 * O número do processo: o novo protocolo, se houve; senão o do início (viabilidade, ou o REDESIM da Rota B e da
 * baixa). Na junta ele não muda depois do protocolo, a não ser que haja novo (Camila, 19/10).
 */
export function protocoloDoProcesso(leg: {
  protocolo_numero?: string | null;
  gop?: string | null;
  gon?: string | null;
}): string | null {
  return leg.protocolo_numero || leg.gop || leg.gon || null;
}

/**
 * A pergunta da exigência entra logo antes da primeira resposta do órgão: entre "Protocolado na junta" e
 * "Deferido" (onde a Juliana marcou); na viabilidade, antes das aprovações.
 */
function comExigencia(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  itens: PlaybookCheck[],
): PlaybookCheck[] {
  if (!etapaDoPlaybook(tipo, coluna)?.exigencia) return itens;
  const i = itens.findIndex(c => ehRespostaDoOrgao(tipo, coluna, c.key));
  if (i < 0) return [...itens, ITEM_EXIGENCIA];
  return [...itens.slice(0, i), ITEM_EXIGENCIA, ...itens.slice(i)];
}

/**
 * Regime escolhido no Levantamento. Varre os blocos como `tiposAlteracaoDoProcesso`; o texto
 * livre de antes da lista é lido pelo nome. `null` quando vazio ou irreconhecível.
 */
export function regimeDoProcesso(
  estadoEtapas: Record<string, unknown> | null | undefined,
): RegimeTributario | null {
  if (!estadoEtapas) return null;
  const bloco = Object.values(estadoEtapas).find(
    b =>
      typeof (b as { regime_pretendido?: unknown } | null | undefined)?.regime_pretendido ===
      'string',
  ) as { regime_pretendido: string } | undefined;
  const texto = (bloco?.regime_pretendido ?? '').toLowerCase();
  if (/\bsimples\b/.test(texto)) return 'Simples Nacional';
  if (/\bpresumido\b/.test(texto)) return 'Lucro Presumido';
  // "real" solto é palavra comum ("faturamento real"): só vale com "lucro" ou sozinho.
  if (/\blucro\s+real\b/.test(texto) || texto.trim() === 'real') return 'Lucro Real';
  // MEI só o valor exato da lista: ele tira os itens de contrato, e texto livre antigo ("sai do MEI", "era MEI")
  // não pode tirar; na dúvida, cobra tudo.
  if (texto.trim() === 'mei') return 'MEI';
  return null;
}

/** Rótulo do campo com o termo do registry por papel (GOP→"Protocolo Web", DARE→emolumentos…). */
export function rotuloComRegistry(
  campo: PlaybookCampo,
  uf?: string | null,
  municipio?: string | null,
): string {
  if (!campo.rotuloRegistry) return campo.label;
  const r = resolverRegistry(uf, municipio);
  if (!r) return campo.label;
  const papel = campo.rotuloRegistry;
  let termo: string | undefined;
  if (papel === 'guia') termo = r.guia.nome;
  else if (papel === 'abertura')
    termo = r.protocoloLabels.abertura ?? r.protocoloLabels.viabilidade;
  else termo = r.protocoloLabels[papel];
  // Termo vazio = UF sem nome de guia confirmado (genérico). Sem este guard o rótulo
  // sairia com um "· " pendurado no fim.
  return termo ? `${campo.label} · ${termo}` : campo.label;
}

/** Opções do input especial "tipos de alteração" (etapa Definição + Alteração). */
export const TIPOS_ALT_OPCOES: { value: TipoAlteracao; label: string }[] = [
  { value: 'endereco', label: 'Endereço' },
  { value: 'razao_social', label: 'Razão social' },
  { value: 'nome_fantasia', label: 'Nome fantasia' },
  { value: 'objeto_social', label: 'Objeto social' },
  // 2026-08-27 (operador). Rota A - viabilidade obrigatoria no REDESIM. NAO tem
  // gerador: o unico instrumento que produzimos e a transformacao EI/MEI -> LTDA,
  // operacao propria do wizard. Marcado sozinho, o card segue o processo com o
  // documento feito fora - os itens "documento gerado"/"auditoria" viram checkbox
  // manual justamente porque nao ha contrato vinculado.
  { value: 'natureza_juridica', label: 'Natureza jurídica' },
  { value: 'abertura_filial', label: 'Abertura de filial' },
  { value: 'extincao_filial', label: 'Extinção de filial' },
  { value: 'alteracao_filial', label: 'Alteração de filial' },
  { value: 'capital', label: 'Capital social' },
  { value: 'quadro_societario', label: 'Quadro de sócios' },
  { value: 'administracao', label: 'Administração' },
];

const DIA_CIVIL_SP = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// `moved_at` é `timestamp without time zone` gravado em UTC e chega sem fuso. O `Date`
// leria isso como hora LOCAL — entrega depois das 21h cairia no dia seguinte.
const SEM_FUSO = /T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** Instante de um carimbo da API; sem fuso = UTC. `null` se inválido. */
export function instanteDaApi(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(SEM_FUSO.test(iso) ? `${iso}Z` : iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Data civil em São Paulo (YYYY-MM-DD) — a régua do semáforo do backend. */
export function dataCivilSP(iso?: string | null): string | null {
  const d = instanteDaApi(iso);
  return d ? DIA_CIVIL_SP.format(d) : null;
}

const diasCivis = (de: string, ate: string): number => {
  const utc = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
  return Math.round((utc(ate) - utc(de)) / 86_400_000);
};

export type PrazoDaEntrega =
  | { situacao: 'sem_prazo' }
  | { situacao: 'no_prazo' }
  | { situacao: 'atrasado'; dias: number };

/**
 * O prazo foi cumprido na ENTREGA? Fato, não alarme: o semáforo compara com hoje e
 * pintaria de atrasado até o processo entregue no prazo. `null` = sem data de entrega.
 */
export function prazoDaEntrega(
  entregueEm?: string | null,
  prazo?: string | null,
): PrazoDaEntrega | null {
  const p = dataCivilSP(prazo);
  if (!p) return { situacao: 'sem_prazo' };
  const e = dataCivilSP(entregueEm);
  if (!e) return null;
  const atraso = diasCivis(p, e);
  return atraso > 0 ? { situacao: 'atrasado', dias: atraso } : { situacao: 'no_prazo' };
}

/** Dias corridos do início à entrega (datas civis em SP). `null` sem uma das datas. */
export function diasAteEntrega(inicio?: string | null, entregueEm?: string | null): number | null {
  const i = dataCivilSP(inicio);
  const e = dataCivilSP(entregueEm);
  return i && e ? Math.max(0, diasCivis(i, e)) : null;
}

/**
 * Humaniza uma duração em DIAS (o backend #761 manda dias fracionários — ex.:
 * 0.0479 dia ≈ 1h). Vira '3 dias' / '5 h' / 'há pouco'. `null` → null.
 */
export function formatDuracao(dias: number | null | undefined): string | null {
  if (dias == null) return null;
  const horas = dias * 24;
  if (horas < 1) return 'há pouco';
  if (horas < 24) return `${Math.round(horas)} h`;
  const d = Math.round(dias);
  return `${d} ${d === 1 ? 'dia' : 'dias'}`;
}

// ============================================================================
// Design tokens compartilhados (board + drawer + header) — espelham as vars do
// mockup pra a cor de cada TIPO e a legenda de FLUXO ficarem consistentes.
// ============================================================================

/**
 * Cor por TIPO — eixo CATEGÓRICO: constituição petróleo · alteração índigo · baixa grafite.
 *
 * 2026-08-11: saiu de verde/laranja/vermelho. Não é gosto — o card passou a codificar
 * RISCO na borda e no fundo (ver `RISCO_META`), e risco alto é vermelho por convenção,
 * sem para onde mover. A paleta antiga colidia em TRÊS pontos: alteração `#C2710C` contra
 * Crítico `#D0510E`, baixa `#D6455D` contra Atrasado `#C22B2B`, constituição `#0F9D6B`
 * contra No prazo. O operador veria vermelho sem saber se é "baixa" ou "atrasado".
 *
 * A regra que separa os eixos: **atributo constante pertence ao container, atributo que
 * varia pertence ao item**. Dentro da aba de constituição todo card é constituição — o
 * tipo vive na aba e na borda superior da coluna, mais um chip discreto no card (que
 * cobre busca global e print fora de contexto), e o card fica livre pra falar de risco.
 *
 * Frios e sem carga de julgamento, de propósito. Grafite na baixa ganha um bônus
 * semântico de brinde: dessaturado lê como encerramento.
 *
 * ⚠️ Estas cores TAMBÉM existem no backend (`legalizacao_kanban_sync._PERFIS`), gravadas
 * em `tb_kanban_projetos.cor` na CRIAÇÃO do projeto — `ensure_projeto` nunca atualiza.
 * Trocar só aqui deixa a aba genérica do /kanban, que espelha o mesmo projeto, com a
 * paleta antiga.
 */
export const COR_TIPO: Record<
  TipoLegalizacao,
  { scheme: string; solid: string; border: string; bg: string; text: string }
> = {
  constituicao: {
    scheme: 'cyan',
    solid: '#0E7490',
    border: '#0E7490',
    bg: '#E2F0F4',
    text: '#0B5A70',
  },
  alteracao: {
    scheme: 'purple',
    solid: '#4338CA',
    border: '#4338CA',
    bg: '#E8E7FA',
    text: '#372FA6',
  },
  baixa: { scheme: 'gray', solid: '#52606D', border: '#52606D', bg: '#E9ECEF', text: '#3E4A54' },
};

// ============================================================================
// RISCO — eixo AVALIATIVO do card. Vem do SLA, e só dele.
// ============================================================================

/** 0 sem risco · 1 atenção · 2 crítico · 3 atrasado. */
export type NivelRisco = 0 | 1 | 2 | 3;

/** Forma mínima do SLA que o board consome (espelha `SlaInfo` do SlaBadge). */
export interface SlaComoRisco {
  cor: 'verde' | 'amarelo' | 'vermelho';
  vencido: boolean;
}

/**
 * Nível de risco do card — mapeamento 1:1 com o SLA que o backend já manda. Sem régua
 * nova, sem fórmula a calibrar (decisão do user, 2026-08-11).
 *
 * O que deliberadamente NÃO entra:
 *  - o prazo de inatividade do órgão (`registry.prazoInatividadeDias`, ex. FIT/JUCERJA):
 *    é prazo LEGAL de terceiro, coisa diferente do acordo interno de atendimento que o
 *    SLA mede. Segue como badge próprio, mas não pinta o card;
 *  - dias parados na etapa, que já aparecem como número.
 *
 * ⚠️ PONTO CEGO CONHECIDO: card sem `expira_em` não tem `sla` e cai em 0. Não é a régua
 * falhando, é dado faltando — por isso a tela mostra "—" no lugar dos dias, em vez de
 * fingir que está tudo bem.
 */
export function nivelRisco(sla?: SlaComoRisco | null): NivelRisco {
  if (!sla) return 0;
  if (sla.vencido) return 3;
  if (sla.cor === 'vermelho') return 2;
  if (sla.cor === 'amarelo') return 1;
  return 0;
}

/**
 * Tokens visuais por nível. `accent` pinta a faixa do card (só nos níveis de alerta), a
 * legenda e a barra da coluna; `seloCor`/`seloBg` são o selo de prazo do card.
 *
 * Quem sobrevive a daltonismo e a print em escala de cinza é o RÓTULO do selo ("Atenção",
 * "Crítico", "Atrasado"): a faixa tem a mesma largura nos três. Até 29/09 o card inteiro
 * era tingido e a borda engrossava nos níveis altos; o padrão do Estatuto e dos Eventos
 * trocou isso por card branco + faixa + selo.
 */
export const RISCO_META: Record<
  NivelRisco,
  { accent: string; rotulo: string; seloCor: string; seloBg: string }
> = {
  0: { accent: '#B8B0A3', rotulo: 'No prazo', seloCor: '#6B645C', seloBg: '#F1EEE9' },
  1: { accent: '#B4750B', rotulo: 'Atenção', seloCor: '#8F5D08', seloBg: '#FBF1DC' },
  2: { accent: '#D0510E', rotulo: 'Crítico', seloCor: '#B0450C', seloBg: '#FCEDE1' },
  3: { accent: '#C22B2B', rotulo: 'Atrasado', seloCor: '#B42626', seloBg: '#FAE7E7' },
};

// ============================================================================
// PRÓXIMA AÇÃO — o que falta agora, e onde está a pendência
// ============================================================================

/**
 * Quem está devendo. `nenhum` = etapa completa, esperando só o avanço.
 * `indefinido` = a coluna não tem bloco no PLAYBOOK — o sistema não sabe, e diz.
 *
 * ⚠️ É `equipe`, não `você` (2026-08-12). O quadro é de um DEPARTAMENTO inteiro: na
 * segunda pessoa, um card cujo responsável é outra pessoa mostrava "VOCÊ" pra quem só
 * estava olhando — afirmação falsa, além de soar invasiva. Os três atores do eixo são
 * equipe / cliente / órgão, e o nome de cada um precisa valer pra qualquer um que abra
 * a tela. Quem responde DENTRO da equipe já está no avatar do card.
 */
export type QuemDeve = 'equipe' | 'orgao' | 'cliente' | 'nenhum' | 'indefinido';

export const QUEM_META: Record<QuemDeve, { rotulo: string; cor: string; bg: string }> = {
  equipe: { rotulo: 'equipe', cor: '#C22B2B', bg: '#FAE7E7' },
  orgao: { rotulo: 'órgão', cor: '#6D4BAF', bg: '#EDE7F8' },
  cliente: { rotulo: 'cliente', cor: '#B4750B', bg: '#FBF1DC' },
  nenhum: { rotulo: 'pronto', cor: '#157F4A', bg: '#E8F3EC' },
  indefinido: { rotulo: '?', cor: '#5A5148', bg: '#E4DFD6' },
};

/**
 * Itens cujo pendente é do CLIENTE (ou dos sócios), não da equipe.
 *
 * ⚠️ A ASSINATURA TEM TRÊS CHAVES DIFERENTES, uma por tipo — e até 2026-08-12 só a de
 * constituição estava aqui. Em alteração e baixa, card parado esperando o cliente
 * assinar dizia que a pendência era da equipe:
 *   constituição → `socios_assinaram`  "Cada sócio assinou (on-portal gov.br)"
 *   alteração    → `todos_assinaram`   "Todos assinaram (on-portal)"
 *   baixa        → `assinado`          "Assinado"
 * Mesmo ato, nomes distintos. Ao mexer no checklist de qualquer tipo, conferir se a
 * chave nova de assinatura entrou aqui — o esquecimento é silencioso: nada quebra, o
 * card só passa a mentir sobre quem está devendo.
 */
const CHECKS_DO_CLIENTE = new Set([
  'cliente_aprovou',
  'socios_assinaram',
  'todos_assinaram',
  'assinado',
  // Quem paga a guia é o CLIENTE (confirmado com o user, 2026-08-12). `dare_gerada` (ato da equipe) saiu em
  // 19/10: o portal só protocola com a guia compensada, e um item de guia basta.
  'dare_paga',
  // Baixa (20/10): quem quita os débitos é o cliente, como a guia.
  'debitos_quitados',
]);

/**
 * Itens que são a RESPOSTA do órgão — a equipe não tem o que fazer além de esperar.
 *
 * Não confundir com o ato de PROTOCOLAR (`protocolado`), que é da equipe: protocolar é
 * ação de quem opera, deferir é resposta da junta. Por isso `protocolado` NÃO está aqui.
 *
 * Antes de existir este conjunto, o bucket `orgao` era inalcançável na prática — ele só
 * disparava com o checklist inteiro marcado, e o que se espera do órgão é justamente o
 * último item a marcar. Enquanto se esperava a junta, o card dizia "equipe"; quando a
 * junta respondia e tudo era marcado, aí sim dizia "órgão". Estava invertido, e dava
 * pra medir: 3 processos em etapa de espera e 0 em "órgão".
 */
const CHECKS_DO_ORGAO = new Set([
  'deferido', // junta defere o ato
  'nome_aprovado', // viabilidade de NOME na junta
  'endereco_aprovado', // viabilidade de ENDEREÇO na prefeitura
  'nome_end_aprovado', // alteração: nome/endereço no mesmo passo
  'lic_emitida', // prefeitura emite o alvará — solicitar (`lic_solicitado`) é da equipe
  'simples_deferido', // a Receita analisa o Simples depois de gerar o CNPJ (FAQ do MAT, 2.6)
]);

/**
 * A etapa inteira é resposta do órgão?
 *
 * ⚠️ Classificar viabilidade por CHAVE não funciona, e o Set acima é prova disso: em
 * etapa `viabilidadeComposta` o checklist efetivo vem dos SUBPASSOS DO REGISTRY, não do
 * playbook, e só GO reusa `nome_aprovado`/`endereco_aprovado`. As outras UFs têm chaves
 * próprias — `balcao_ok` (SP-capital), `viab_ok` (SP-interior; até 2026-08-27 também
 * `lic_ok`, que saiu da viabilidade), `cpl_deferida`
 * + `viab_junta_ok` (RJ) — e nenhuma delas estava no Set. Resultado medido: a inversão
 * que este módulo corrigiu para GO seguia de pé em SP e RJ, com o card dizendo
 * "EQUIPE · Falta: Registro integrado deferido" enquanto se esperava a JUCESP.
 *
 * Aqui a classificação é por CONTEXTO, não por chave: viabilidade é, por definição, o
 * órgão analisando — todo item obrigatório dela é resposta dele ("aprovado", "deferido",
 * "concluído"). Registry novo entra classificado sem tocar em lista nenhuma.
 *
 * NÃO vale para `orgaosDoRegistry` ("Demais órgãos"): lá os itens `org_*` são inscrições
 * que a EQUIPE faz nos órgãos, não respostas que ela espera.
 */
function etapaTodaDoOrgao(tipo: TipoLegalizacao, coluna: string | null | undefined): boolean {
  return !!etapaDoPlaybook(tipo, coluna)?.viabilidadeComposta;
}

function ehRespostaDoOrgao(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  key: string,
): boolean {
  return CHECKS_DO_ORGAO.has(key) || etapaTodaDoOrgao(tipo, coluna);
}

/** As respostas do órgão entre os itens: o que o "Não" da exigência marca. */
export function respostasDoOrgao(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  itens: PlaybookCheck[],
): PlaybookCheck[] {
  return itens.filter(c => c.key !== CHAVE_EXIGENCIA && ehRespostaDoOrgao(tipo, coluna, c.key));
}

/**
 * A linha de ação do card: o que falta AGORA e onde está a pendência.
 *
 * Substitui "N dias nesta etapa · M no total" — que dizia onde o processo está, coisa
 * que a coluna já diz. O tempo migra pro rodapé.
 *
 * Precedência, e o porquê de cada degrau:
 *  1. etapa sem bloco no playbook → `indefinido`. FAIL-CLOSED: o card ADMITE que não
 *     sabe, em vez de mostrar barra vazia e parecer completo. É a face visual do
 *     `etapaConfigurada` — o gate já bloqueia o avanço, aqui a tela explica por quê;
 *  2. pendente da EQUIPE → `equipe`, com o rótulo do primeiro;
 *     2b. exigência aberta ou sem texto, ou sem resposta com o órgão já respondido → `equipe`;
 *  3. sobrou só pendente do cliente → `cliente`;
 *  4. sobrou só resposta do órgão → `orgao`, dizendo O QUE se espera;
 *  5. nada pendente → `nenhum`.
 *
 * ⚠️ A ordem 2 antes de 3 não é detalhe. O aceite do cliente é o ÚLTIMO item do
 * checklist da etapa fundida, então com o card recém-criado ele está pendente junto com
 * todo o resto — e apontar "cliente" ali seria mentira: não há o que aprovar antes de o
 * documento existir. A bola só passa pra ele quando os pendentes RESTANTES são só dele.
 * (Peguei isso com teste; a primeira versão checava o cliente primeiro.)
 *
 * O 4 vir depois do 2 e do 3 tem a mesma lógica: numa etapa de espera, se ainda falta
 * algo da equipe ou do cliente, não adianta culpar a Junta por item que ninguém
 * preencheu.
 *
 * ⚠️ 2026-08-12 — o degrau 4 ANTES media outra coisa: `orgao` só saía com o checklist
 * INTEIRO marcado numa etapa `wait`. Como o que se espera do órgão (`deferido`,
 * `nome_aprovado`…) é justamente o último item a marcar, o bucket era inalcançável
 * enquanto de fato se esperava, e disparava só depois que o órgão já tinha respondido —
 * invertido. Agora quem manda é o CONJUNTO a que o item pendente pertence
 * (`CHECKS_DO_ORGAO`), não o marcador de fluxo da etapa. O `wait` virou só o caso
 * terminal: nada pendente numa etapa de espera = o órgão respondeu, é `nenhum`.
 */
export function proximaAcao(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
  estado: Record<string, unknown> | null | undefined,
  opts?: {
    uf?: string | null;
    municipio?: string | null;
    tiposAlteracao?: TipoAlteracao[] | null;
    regime?: RegimeTributario | null;
  },
): { quem: QuemDeve; texto: string; restantes: number } {
  if (!etapaConfigurada(tipo, coluna))
    return { quem: 'indefinido', texto: 'Etapa não configurada — avanço bloqueado', restantes: 0 };

  const st = estado ?? {};
  const checklist = checklistEfetivoDaEtapa(
    tipo,
    coluna,
    opts?.uf,
    opts?.municipio,
    opts?.tiposAlteracao,
    opts?.regime,
  );
  const pendentes = checklist.filter(c => c.req && !itemSatisfeito(st, c.key));
  const restantes = pendentes.length - 1;
  // A pergunta da exigência fica fora dos baldes: de quem é a bola depende da RESPOSTA (abaixo).
  const outros = pendentes.filter(c => c.key !== CHAVE_EXIGENCIA);
  const ehDoOrgao = (k: string) => ehRespostaDoOrgao(tipo, coluna, k);

  const daEquipe = outros.filter(c => !CHECKS_DO_CLIENTE.has(c.key) && !ehDoOrgao(c.key));
  if (daEquipe.length) return { quem: 'equipe', texto: `Falta: ${daEquipe[0].label}`, restantes };

  // Exigência aberta: cumprir é da equipe, e o card diz qual — é o motivo de estar parado. Sem resposta
  // com o órgão já respondido (Deferido marcado), falta só dizer se houve.
  if (checklist.some(c => c.key === CHAVE_EXIGENCIA)) {
    const resposta = st[CHAVE_EXIGENCIA];
    const exigencia = textoDaExigencia(st);
    const orgaoPendente = outros.some(c => ehDoOrgao(c.key));
    if (resposta === 'Sim' && !exigencia)
      return { quem: 'equipe', texto: 'Falta: descrever a exigência', restantes };
    if (resposta === 'Sim' && orgaoPendente)
      return { quem: 'equipe', texto: `Exigência: ${exigencia}`, restantes };
    if (resposta !== 'Sim' && resposta !== 'Não' && !orgaoPendente)
      return { quem: 'equipe', texto: 'Falta: responder se houve exigência', restantes };
  }
  // Simples respondido "Não" sem o motivo: quem deve é a equipe (escrever o motivo), não a Receita.
  if (
    pendentes.some(c => c.key === CHAVE_SIMPLES) &&
    st[CHAVE_SIMPLES] === 'Não' &&
    !motivoDoSimples(st)
  )
    return { quem: 'equipe', texto: 'Falta: motivo do Simples indeferido', restantes };

  const doCliente = outros.filter(c => CHECKS_DO_CLIENTE.has(c.key));
  // ⚠️ `Falta:` como no ramo da equipe. Era o ÚNICO dos quatro ramos sem palavra de
  // estado — `equipe` diz "Falta:", `orgao` diz "Aguardando:", `nenhum` diz "pode
  // avançar", e este devolvia o rótulo pelado. Num card real de produção o rodapé lia
  // "Cliente aprovou o documento gerado", que afirma o CONTRÁRIO do que significa: não
  // aprovou ainda, é o que está travando. O crachá "CLIENTE" ao lado já diz de quem é a
  // bola; o que faltava era dizer que ainda não aconteceu.
  if (doCliente.length)
    return { quem: 'cliente', texto: `Falta: ${doCliente[0].label}`, restantes };

  // Sobrou só o que depende do órgão. O texto vem do ITEM, não de um genérico
  // "Aguardando o órgão": saber que se espera o DEFERIMENTO é diferente de saber que se
  // espera a aprovação do NOME — são etapas e prazos distintos.
  const doOrgao = outros.filter(c => ehDoOrgao(c.key));
  if (doOrgao.length) return { quem: 'orgao', texto: `Aguardando: ${doOrgao[0].label}`, restantes };

  // Nada pendente. Numa etapa de espera isso significa que o órgão já respondeu e só
  // falta avançar o card — por isso `nenhum`, e não `orgao` como era antes.
  if (fluxoDaEtapa(tipo, coluna).includes('wait'))
    return { quem: 'nenhum', texto: 'Órgão respondeu — pode avançar', restantes: 0 };

  return { quem: 'nenhum', texto: 'Etapa concluída — pode avançar', restantes: 0 };
}

/** Legenda de cada marcador de fluxo (rótulo + descrição curta + cor do ícone). */
export const FLUXO_META: Record<FluxoMarker, { label: string; descricao: string; cor: string }> = {
  doc: { label: 'Gera o documento', descricao: 'usa o gerador atual', cor: '#1B88F5' },
  wait: { label: 'Espera externa', descricao: 'conta prazo (SLA)', cor: '#C2710C' },
  auto: { label: 'Automação futura', descricao: 'Fase 2, read-only', cor: '#7C3AED' },
  cond: { label: 'Etapa condicional', descricao: 'só se aplicável', cor: '#64748B' },
  rpa_read: { label: 'RPA leitura', descricao: 'Fase 2: robô consulta o órgão', cor: '#0D9488' },
  rpa_write: { label: 'RPA escrita', descricao: 'Fase 2: robô submete ao órgão', cor: '#C026D3' },
};

/** Ordem canônica dos marcadores (usada na legenda). */
// `auto` fica FORA da legenda (legado — nenhuma etapa usa; ver FluxoMarker).
export const FLUXO_ORDEM: FluxoMarker[] = ['doc', 'wait', 'cond', 'rpa_read', 'rpa_write'];

/** Marcadores de fluxo da etapa (tipo + coluna) — [] se a etapa não tem. */
export function fluxoDaEtapa(
  tipo: TipoLegalizacao,
  coluna: string | null | undefined,
): FluxoMarker[] {
  return etapaDoPlaybook(tipo, coluna)?.fluxo ?? [];
}

// ============================================================================
// "Iniciar aqui" — criar processo direto numa etapa avançada (operador já fez as
// anteriores FORA do sistema). Modelo HÍBRIDO: as etapas puladas são marcadas como
// ASSUMIDAS (estado distinto de concluída-por-checklist) e coletamos só os
// campos-chave (identificadores subtype) que as etapas seguintes referenciam.
// ============================================================================

/** Marca em `estado_etapas[coluna]` indicando etapa assumida (feita fora do sistema). */
export const ETAPA_ASSUMIDA_KEY = '_assumida';

/**
 * Chave RESERVADA (topo de `estado_etapas`, não é coluna) da observação ANTIGA — um
 * texto só, sem autor nem data. Desde 29/09 as observações são os comentários do card
 * do kanban; esta fica só para leitura do que já foi escrito.
 */
export const CARD_OBS_KEY = '_obs';

/** Observação antiga, salva em `estado_etapas._obs` ('' se não houver). */
export function obsDoCard(estadoEtapas: Record<string, unknown> | null | undefined): string {
  const v = (estadoEtapas as Record<string, unknown> | null | undefined)?.[CARD_OBS_KEY];
  return typeof v === 'string' ? v : '';
}

/**
 * O que a faixa do card mostra: o último comentário do card, com quem escreveu. Sem
 * comentário, a observação antiga — que não tem autor gravado e leva o responsável do
 * processo, como na aba Comentários (decisão do usuário, 02/10).
 */
export function observacaoDoCard(leg: {
  estado_etapas: Record<string, unknown> | null;
  ultimo_comentario?: { texto: string; autor_nome: string | null } | null;
  responsavel_nome?: string | null;
}): { texto: string; autor: string | null } | null {
  const ultimo = leg.ultimo_comentario;
  if (ultimo?.texto.trim()) return { texto: ultimo.texto, autor: ultimo.autor_nome };
  const antiga = obsDoCard(leg.estado_etapas);
  return antiga.trim() ? { texto: antiga, autor: leg.responsavel_nome ?? null } : null;
}

/**
 * Chave RESERVADA (topo de `estado_etapas`) p/ a governança do PRAZO manual (SLA). O
 * prazo em si vira `card.expira_em` (o semáforo backend lê de lá); aqui fica só o
 * HISTÓRICO de reajustes (1ª definição livre; alterar exige motivo + registra).
 */
export const CARD_SLA_KEY = '_sla';

/**
 * `<input type="date">` (YYYY-MM-DD) → ISO gravado em `card.expira_em`.
 *
 * MEIO-DIA UTC, não meia-noite: o semáforo do backend compara só a parte de data em
 * America/Sao_Paulo (UTC-3). Gravar `T00:00:00Z` faria a data civil retroceder um dia
 * no fuso local — o prazo escolhido para o dia 30 apareceria como 29 e venceria cedo.
 * Meio-dia sobra folga para os dois lados.
 *
 * Vive aqui porque tem DOIS escritores: o bloco "Prazo (SLA)" do drawer e o campo de
 * prazo na criação do processo. Divergir entre eles produziria cards com um dia de
 * diferença dependendo de onde a data foi digitada.
 */
export const dataInputParaIso = (ymd: string): string => `${ymd}T12:00:00Z`;

/**
 * Mensagem que o backend mandou, quando mandou — senão o texto genérico.
 *
 * O FastAPI põe a explicação em `response.data.detail`. Sem isto, uma recusa por REGRA DE
 * NEGÓCIO (422: "CNPJ é obrigatório em alteração e baixa") chega ao operador como "tente
 * novamente" — ele repete a mesma ação até desistir, sem nunca saber o motivo.
 *
 * ⚠️ Vive aqui porque TODA mutation que fala com endpoint capaz de 422 precisa dele. Numa
 * primeira versão só o `salvarMutation` do drawer usava, e o `avancar` (mesmo endpoint,
 * mesmo PATCH) seguia engolindo — dois caminhos pro mesmo erro, com mensagens diferentes.
 *
 * Erro de VALIDAÇÃO do Pydantic devolve `detail` como LISTA, não string; o teste de tipo
 * abaixo cobre isso caindo no genérico em vez de imprimir "[object Object]".
 */
export function detalheDoErro(
  erro: unknown,
  generico = 'Não foi possível salvar. Tente novamente.',
): string {
  const detail = (erro as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : generico;
}

/**
 * CNPJ válido? Confere os DOIS dígitos verificadores, não só o tamanho.
 *
 * ⚠️ NÃO reusa `isValidCNPJ` de `utils/documentValidation`: aquele faz
 * `value.replace(/\D/g, '')` e exige 14 DÍGITOS, então rejeita o CNPJ ALFANUMÉRICO
 * (12 caracteres alfanuméricos + 2 dígitos verificadores) que a Receita passou a
 * emitir. Aqui cada caractere vale `código ASCII − 48`, que para '0'–'9' dá exatamente
 * o próprio número — ou seja, o resultado é idêntico ao do algoritmo clássico nos CNPJs
 * antigos, e continua correto nos novos.
 *
 * Validado contra os 9 registros do banco de dev: os 8 reais passam, o de teste com
 * 2 caracteres não.
 */
export function cnpjValido(v?: string | null): boolean {
  const s = (v ?? '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (s.length !== 14) return false;
  if (!/^\d{2}$/.test(s.slice(12))) return false; // os 2 DVs são sempre numéricos
  if (/^(.)\1{13}$/.test(s)) return false; // repetido passa no módulo 11 mas não existe
  const val = s.split('').map(c => c.charCodeAt(0) - 48);
  const dv = (pesos: number[]): number => {
    const r = pesos.reduce((acc, p, i) => acc + val[i] * p, 0) % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const p1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const p2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  return dv(p1) === val[12] && dv(p2) === val[13];
}

/**
 * CNPJ mascarado para leitura. `null` quando não há valor.
 *
 * ⚠️ NÃO reusa `formatCNPJ` de `utils/FormatFunctions`: aquele faz `padStart(14, '0')`
 * antes de mascarar, então fabrica um CNPJ plausível a partir de qualquer sujeira —
 * "123" sairia como "00.000.000/0001-23". Aqui o campo é texto livre no drawer (sem
 * máscara e sem validação) e o formato gravado é misto: a criação faz strip de dígitos,
 * o drawer grava verbatim. Só mascara quando há exatamente 14 dígitos; qualquer outra
 * coisa volta como veio, pro operador enxergar o que está de fato no banco em vez de
 * uma máscara bonita por cima de dado torto.
 */
export function formatarCnpj(v?: string | null): string | null {
  if (!v) return null;
  // ⚠️ ALFANUMÉRICO, não `\D`. O CNPJ da IN RFB 2.229/2024 aceita LETRA nas 12
  // primeiras posições (só os 2 dígitos verificadores seguem numéricos). Com
  // `replace(/\D/g,'')` a letra era DESCARTADA e um CNPJ de 14 caracteres com letra
  // virava outro número, de 13 dígitos ou menos — mascarado ou não, errado.
  //
  // Isto já valia antes desta função ser usada em campo editável: o cabeçalho do drawer
  // e o card do quadro exibem por aqui. O fix de alfanumérico de 2026-08 (`960447a1`)
  // passou por `utils/documento.ts` e pelo wizard, mas NÃO por este arquivo.
  //
  // Caixa alta acompanha o backend (`_norm_cnpj` faz `.upper()`), que é a forma canônica
  // da norma. Dígito não é afetado.
  const d = v.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
  if (d.length !== 14) return v;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Data de hoje + N dias, no formato do `<input type="date">`. */
export function dataEmDias(dias: number): string {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Uma entrada do histórico de reajuste do prazo. */
export interface SlaHistoricoEntry {
  de: string | null; // prazo anterior (ISO) — null na 1ª definição
  para: string; // novo prazo (ISO)
  motivo: string; // '' na 1ª definição
  por: string; // nome de quem alterou
  quando: string; // ISO do reajuste
}

/** Histórico de reajustes do prazo (`estado_etapas._sla.historico`), [] se não houver. */
export function slaHistorico(
  estadoEtapas: Record<string, unknown> | null | undefined,
): SlaHistoricoEntry[] {
  const sla = (estadoEtapas as Record<string, { historico?: unknown }> | null | undefined)?.[
    CARD_SLA_KEY
  ];
  return Array.isArray(sla?.historico) ? (sla?.historico as SlaHistoricoEntry[]) : [];
}

/** True se a etapa (coluna) do processo foi marcada como assumida. */
export function etapaAssumida(
  estadoEtapas: Record<string, unknown> | null | undefined,
  coluna: string,
): boolean {
  const est = (estadoEtapas as Record<string, Record<string, unknown>> | null | undefined)?.[
    coluna
  ];
  return !!est?.[ETAPA_ASSUMIDA_KEY];
}

/** Alguma etapa foi assumida? (processo cadastrado no meio do caminho — início desconhecido) */
export function temEtapaAssumida(
  estadoEtapas: Record<string, unknown> | null | undefined,
): boolean {
  return Object.values(estadoEtapas ?? {}).some(
    b => !!b && typeof b === 'object' && !!(b as Record<string, unknown>)[ETAPA_ASSUMIDA_KEY],
  );
}

/**
 * Campos-chave (subtype) das etapas ANTERIORES à `alvoIdx` — os identificadores que
 * as etapas seguintes usam (gop/gon/protocolo/nire/cnpj/rota/dare…). Dedup por key.
 * Usado pelo "Iniciar aqui" pra pedir SÓ o que importa (não todos os campos/checklists).
 */
export function camposChaveAnteriores(
  tipo: TipoLegalizacao,
  colunas: string[],
  alvoIdx: number,
): PlaybookCampo[] {
  const out: PlaybookCampo[] = [];
  const vistos = new Set<string>();
  colunas.slice(0, Math.max(0, alvoIdx)).forEach(col => {
    (etapaDoPlaybook(tipo, col)?.campos ?? []).forEach(c => {
      if (c.origem === 'subtype' && !vistos.has(c.key)) {
        vistos.add(c.key);
        out.push(c);
      }
    });
  });
  return out;
}
